import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Trash2,
  Eye,
  Pencil,
  Sparkles,
  Copy,
  CalendarDays,
  UtensilsCrossed,
  History,
  Save,
  Download,
  StickyNote,
  Info,
  BookOpen,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  companyAdminCustomersAPI,
  companyAdminRecipesAPI,
} from "../../lib/companyAdminApi";
import { useCompanyAdminAuth } from "../../contexts/CompanyAdminAuthContext";
import { resolveCustomerLocation } from "../../lib/customerRegionFlag";
import {
  calcMacroFromPer100,
  initMeasurementFromFoodDetail,
  initMeasurementFromStoredFood,
  measurementsForBase,
  recalculateForNewUnit,
  recalculateWeight,
} from "../../lib/unitConversion";

const formatDuration = (fromDate, tillDate) => {
  try {
    const a = new Date(fromDate);
    const b = new Date(tillDate);
    const days =
      Math.ceil((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return `${Math.max(days, 1)} days`;
  } catch {
    return "-";
  }
};

/** 0=Inactive, 1=Active, 2=Expired, 3=Soft Delete */
const dietStatusLabel = (status) => {
  const s = Number(status);
  if (s === 1) return "Active";
  if (s === 0) return "Inactive";
  if (s === 2) return "Expired";
  if (s === 3) return "Soft Deleted";
  return "Unknown";
};

const dietStatusClassName = (status) =>
  Number(status) === 1
    ? "text-green-600 font-semibold"
    : "text-red-600 font-semibold";

const isoDate = (d) => d.toISOString().slice(0, 10);

/** Local datetime string (no TZ) — same contract as mobile createManualFood + parseClientDate. */
const clientNowDateString = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/** JS getDay(): 0=Sun … 6=Sat — UI order Mon→Sun */
const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];
const ALL_WEEKDAY_VALUES = [0, 1, 2, 3, 4, 5, 6];

const toDateInputValue = (v) => {
  if (!v) return "";
  if (v instanceof Date) return isoDate(v);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : isoDate(d);
};

const normalizeWeekdaysState = (plan) => {
  const days = Array.isArray(plan?.weekdays)
    ? plan.weekdays.map(Number).filter((d) => d >= 0 && d <= 6)
    : [];
  const unique = [...new Set(days)].sort((a, b) => a - b);
  const mode =
    plan?.assignment_mode === "custom" && unique.length > 0 && unique.length < 7
      ? "custom"
      : "weekly";
  return {
    assignmentMode: mode,
    weekdays: mode === "weekly" ? [...ALL_WEEKDAY_VALUES] : unique,
  };
};

const formatWeekdaysLabel = (plan) => {
  const { assignmentMode, weekdays } = normalizeWeekdaysState(plan);
  if (assignmentMode === "weekly" || weekdays.length === 7) return "Weekly";
  return (
    WEEKDAY_OPTIONS.filter((o) => weekdays.includes(o.value))
      .map((o) => o.label)
      .join(", ") || "Weekly"
  );
};

/** Date range + weekly / selected weekdays picker for strict plan foods */
const StrictSchedulePicker = ({
  fromDate,
  tillDate,
  onFromChange,
  onTillChange,
  assignmentMode,
  weekdays,
  onModeChange,
  onWeekdaysChange,
}) => {
  const toggleDay = (day) => {
    if (assignmentMode !== "custom") onModeChange("custom");
    const set = new Set(weekdays);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    const next = [...set].sort((a, b) => a - b);
    onWeekdaysChange(next.length ? next : [day]);
  };

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-primary" />
        <div className="text-sm font-semibold text-foreground">Schedule</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-foreground">From date</Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => onFromChange(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-foreground">Till date</Label>
          <Input
            type="date"
            value={tillDate}
            onChange={(e) => onTillChange(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label className="text-foreground">Assign food</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              onModeChange("weekly");
              onWeekdaysChange([...ALL_WEEKDAY_VALUES]);
            }}
            className={
              "rounded-md border px-3 py-2.5 text-sm font-medium text-left transition-colors " +
              (assignmentMode === "weekly"
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-muted/40")
            }
          >
            <div>Whole week</div>
            <div className="text-xs font-normal text-muted-foreground mt-0.5">
              Same food every day
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              onModeChange("custom");
              if (!weekdays.length || weekdays.length === 7) {
                onWeekdaysChange([1, 2, 3, 4, 5]);
              }
            }}
            className={
              "rounded-md border px-3 py-2.5 text-sm font-medium text-left transition-colors " +
              (assignmentMode === "custom"
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-muted/40")
            }
          >
            <div>Selected days</div>
            <div className="text-xs font-normal text-muted-foreground mt-0.5">
              Pick weekdays only
            </div>
          </button>
        </div>
      </div>

      {assignmentMode === "custom" && (
        <div>
          <div className="text-xs text-muted-foreground mb-2">
            Applies only on selected days inside the from/till date range.
          </div>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_OPTIONS.map((d) => {
              const on = weekdays.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={
                    "h-9 min-w-[2.75rem] px-2 rounded-full border text-xs font-semibold transition-colors " +
                    (on
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-card text-muted-foreground hover:border-primary/40")
                  }
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {assignmentMode === "weekly" && (
        <div className="text-xs text-muted-foreground">
          Food repeats Mon–Sun between the from and till dates.
        </div>
      )}
    </div>
  );
};

const normalizePortionInput = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const planLabelFromType = (t) =>
  t === 1
    ? "Strict Diet Plan"
    : t === 2
      ? "Moderate Diet Plan"
      : "Flexible Diet Plan";

/** Strict plan row: Food stores per-unit macros; UI shows macros × plan portion */
function strictPlanPortionMultiplier(plan) {
  const n = Number(plan?.portion);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function scaleFoodMacroForStrictRow(baseValue, plan) {
  const b = Number(baseValue);
  if (!Number.isFinite(b)) return null;
  const m = strictPlanPortionMultiplier(plan);
  return Math.round(b * m * 100) / 100;
}

/** Single-line + ellipsis; full text on hover (strict plan table) */
function StrictPlanFoodNameCell({ name }) {
  const text = name != null && String(name).trim() ? String(name).trim() : "—";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="block w-full min-w-[10rem] max-w-[min(22rem,48vw)] truncate cursor-default text-left align-middle outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm"
          tabIndex={0}
        >
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm text-left break-words">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

// ==================== Strict helpers ====================
const FoodSearch = ({
  value,
  onChange,
  onSelect,
  selectedFood,
  source = "food_bank",
}) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const q = value.trim();

    if (selectedFood && q === selectedFood.product_name) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (!q) {
      setResults([]);
      setOpen(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const t = setTimeout(
      async () => {
        try {
          let res;
          if (source === "usda") {
            res = await companyAdminCustomersAPI.usdaSearch({ q, limit: 20 });
            if (cancelled) return;
            setResults(res.data.results || []);
          } else {
            res = await companyAdminCustomersAPI.foodbankSearch({ q });
            if (cancelled) return;
            setResults(res.data.results || []);
          }
          setOpen(true);
        } catch {
          if (cancelled) return;
          setResults([]);
          setOpen(false);
        } finally {
          if (cancelled) return;
          setLoading(false);
        }
      },
      source === "usda" ? 600 : 350,
    );

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value, selectedFood, source]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search food..."
        />
        {loading && (
          <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-sm max-h-60 overflow-y-auto">
          {results.map((r) => (
            <button
              key={`${r.food_bank}-${r.id}`}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
              onClick={() => {
                onSelect(r);
                setOpen(false);
              }}
            >
              <div className="font-medium text-foreground flex items-center gap-1.5">
                <span>{r.product_name}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {r.serving || ""}{" "}
                {r.food_bank === "usda"
                  ? `• ${r.calories}kcal • F:${r.fat}g C:${r.carbs}g P:${r.protein}g`
                  : r.calories != null ||
                      r.fat != null ||
                      r.carbs != null ||
                      r.protein != null
                    ? `• ${r.calories ?? "-"}kcal • F:${r.fat ?? "-"}g C:${r.carbs ?? "-"}g P:${r.protein ?? "-"}g`
                    : `• ${r.food_bank}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const formatMicroValue = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const value = v.value ?? v.amount ?? v.val ?? v.quantity;
    const unit = v.unit ?? v.uom;
    if (value !== undefined && unit) return `${value} ${unit}`;
    if (value !== undefined) return String(value);
    return JSON.stringify(v);
  }
  return String(v);
};

const looksLikeMicroGroup = (v) => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  if (
    "value" in v ||
    "unit" in v ||
    "amount" in v ||
    "quantity" in v ||
    "uom" in v
  )
    return false;
  return true;
};

const normalizeMicronutrients = (micros) => {
  const rows = [];
  const obj = micros && typeof micros === "object" ? micros : {};

  Object.entries(obj).forEach(([k, v]) => {
    if (looksLikeMicroGroup(v)) {
      Object.entries(v).forEach(([nk, nv]) => {
        const value =
          nv && typeof nv === "object"
            ? (nv.value ?? nv.amount ?? nv.val ?? nv.quantity ?? "")
            : (nv ?? "");
        const unit =
          nv && typeof nv === "object" ? (nv.unit ?? nv.uom ?? "") : "";
        rows.push({
          category: k,
          nutrient: nk,
          value: value === null || value === undefined ? "" : String(value),
          unit: unit || "",
        });
      });
      return;
    }

    const value =
      v && typeof v === "object"
        ? (v.value ?? v.amount ?? v.val ?? v.quantity ?? "")
        : (v ?? "");
    const unit = v && typeof v === "object" ? (v.unit ?? v.uom ?? "") : "";
    rows.push({
      category: "",
      nutrient: k,
      value: value === null || value === undefined ? "" : String(value),
      unit: unit || "",
    });
  });

  return rows;
};

const buildMicronutrientsObject = (rows) => {
  const out = {};

  const parseVal = (s) => {
    if (s === "" || s === null || s === undefined) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : s;
  };

  (rows || []).forEach((r) => {
    const val = parseVal(r.value);
    if (r.category) {
      if (!out[r.category]) out[r.category] = {};
      out[r.category][r.nutrient] = r.unit ? { value: val, unit: r.unit } : val;
      return;
    }
    out[r.nutrient] = r.unit ? { value: val, unit: r.unit } : val;
  });

  return out;
};

const normalizeStrictSubFoodList = (food) => {
  const raw = food?.sub_food_list;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.filter((x) => x != null && typeof x === "object");
};

const formatSubFoodQuantityLabel = (item) => {
  if (!item || typeof item !== "object") return "";
  if (item.quantity != null && String(item.quantity).trim()) {
    return String(item.quantity).trim();
  }
  const tq = item.total_quantity;
  if (tq && typeof tq === "object" && tq.value != null) {
    const u = tq.unit != null ? String(tq.unit).trim() : "";
    return `${tq.value}${u ? ` ${u}` : ""}`.trim();
  }
  if (item.size != null && String(item.size).trim()) {
    return String(item.size).trim();
  }
  return "";
};

const formatSubFoodMacroSummary = (item) => {
  if (!item || typeof item !== "object") return "—";
  const pickNum = (v) => {
    if (v == null || v === "") return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };
  const kcal = pickNum(item.total_calories ?? item.calories);
  const p = pickNum(item.total_proteins ?? item.proteins);
  const f = pickNum(item.total_fat ?? item.fat);
  const c = pickNum(item.total_carbs ?? item.carbs);
  const parts = [];
  if (kcal != null) parts.push(`${kcal} kcal`);
  if (p != null) parts.push(`P ${p}g`);
  if (f != null) parts.push(`F ${f}g`);
  if (c != null) parts.push(`C ${c}g`);
  return parts.length ? parts.join(" · ") : "—";
};

const SubfoodsModal = ({ open, onClose, foodName, items }) => {
  if (!open) return null;
  const list = Array.isArray(items) ? items : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-3xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">
            Subfoods{foodName ? ` — ${foodName}` : ""}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>
        <div className="app-modal-body p-6 overflow-y-auto flex-1 min-h-0">
          {list.length === 0 ? (
            <div className="text-sm text-muted-foreground">No subfoods listed.</div>
          ) : (
            <ol className="rounded-md border border-border divide-y divide-border list-none m-0 p-0">
              {list.map((item, idx) => {
                const name =
                  (item.name && String(item.name).trim()) ||
                  `Component ${idx + 1}`;
                const qty = formatSubFoodQuantityLabel(item);
                const desc =
                  item.description != null && String(item.description).trim()
                    ? String(item.description).trim()
                    : "";
                return (
                  <li
                    key={`sub-${idx}`}
                    className="px-4 py-3 text-sm hover:bg-muted"
                  >
                    <div className="font-medium text-foreground">
                      <span className="text-muted-foreground font-normal tabular-nums mr-2">
                        {idx + 1}.
                      </span>
                      {name}
                    </div>
                    {qty ? (
                      <div className="text-muted-foreground mt-0.5">Qty: {qty}</div>
                    ) : null}
                    {desc ? (
                      <div className="text-muted-foreground mt-0.5">{desc}</div>
                    ) : null}
                    <div className="text-muted-foreground mt-1 text-xs">
                      {formatSubFoodMacroSummary(item)}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const MicronutrientsModal = ({ open, onClose, micronutrients }) => {
  if (!open) return null;
  const entries = Object.entries(micronutrients || {});

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-3xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">
            Micronutrients
          </h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>
        <div className="app-modal-body p-6 flex-1 min-h-0 overflow-y-auto">
          {entries.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No micronutrients available.
            </div>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted">
                    <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                      Nutrient
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                      Value
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map(([k, v]) => (
                    <TableRow key={k} className="hover:bg-muted">
                      <TableCell className="font-medium text-foreground">
                        {k}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatMicroValue(v)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const NumberField = ({ label, value, onChange, placeholder }) => (
  <div>
    <Label className="text-foreground">{label}</Label>
    <Input
      type="number"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) =>
        onChange(e.target.value === "" ? "" : Number(e.target.value))
      }
    />
  </div>
);

const EditFoodModal = ({ open, onClose, plan, onSaved, editMode = "strict" }) => {
  const isMealEdit = editMode === "meal";
  const [saving, setSaving] = useState(false);
  const [itemType, setItemType] = useState("food");
  const isSupplement = isMealEdit && itemType === "supplement";
  const [dosePerDay, setDosePerDay] = useState(1);
  const [molecularName, setMolecularName] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");
  const [portion, setPortion] = useState(1);

  // Mobile-style measurement
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [perItemWeight, setPerItemWeight] = useState(100);
  const [selectedWeight, setSelectedWeight] = useState(100);
  const [selectedUnit, setSelectedUnit] = useState("Gram");
  const [baseUnit, setBaseUnit] = useState("g");
  const [per100, setPer100] = useState({
    calories: 0,
    fat: 0,
    protein: 0,
    carbs: 0,
  });

  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const [planStatus, setPlanStatus] = useState(1);

  const [hasMicros, setHasMicros] = useState(false);
  const [microRows, setMicroRows] = useState([]);

  const applyMacrosFromMeasurement = (weight, nextPer100) => {
    const w = Number(weight) || 0;
    const p = nextPer100 || per100;
    setCalories(calcMacroFromPer100(p.calories, w));
    setFat(calcMacroFromPer100(p.fat, w));
    setCarbs(calcMacroFromPer100(p.carbs, w));
    setProtein(calcMacroFromPer100(p.protein, w));
  };

  useEffect(() => {
    if (!open) return;
    const f = plan?.food || {};

    setItemType(
      String(plan?.item_type || "food").toLowerCase() === "supplement"
        ? "supplement"
        : "food",
    );
    setDosePerDay(Number(plan?.dose_per_day) === 2 ? 2 : 1);
    setMolecularName(plan?.molecular_name || f.molecular_name || "");
    setCompanyName(plan?.company_name || f.company_name || "");
    setName(f.name || plan?.name || "");
    setDescription(f.description || plan?.description || "");
    setClientNote(plan?.client_note || "");

    setFromDate(
      toDateInputValue(plan?.from_date) || isoDate(new Date()),
    );
    setTillDate(
      toDateInputValue(plan?.till_date) ||
        isoDate(addDays(new Date(), 7)),
    );
    const schedule = normalizeWeekdaysState(plan);
    setAssignmentMode(schedule.assignmentMode);
    setWeekdays(schedule.weekdays);
    setPlanStatus(Number(plan?.status ?? 1));
    setPortion(normalizePortionInput(plan?.portion ?? 1));

    const micros = f.micronutrients || plan?.micronutrients;
    const has = !!(
      micros &&
      typeof micros === "object" &&
      Object.keys(micros).length > 0
    );
    setHasMicros(has);
    setMicroRows(has ? normalizeMicronutrients(micros) : []);

    const init = initMeasurementFromStoredFood(f);
    setPer100(init.per100);
    setSelectedQuantity(init.selectedQuantity);
    setPerItemWeight(init.perItemWeight);
    setSelectedWeight(init.selectedWeight);
    setSelectedUnit(init.selectedUnit);
    setBaseUnit(init.baseUnit);
    applyMacrosFromMeasurement(init.selectedWeight, init.per100);
  }, [open, plan]);

  const handleQuantityChange = (v) => {
    setSelectedQuantity(v);
    const q = Number(v);
    if (!Number.isFinite(q) || q <= 0) return;
    const w = recalculateWeight(q, perItemWeight);
    setSelectedWeight(w);
    applyMacrosFromMeasurement(w);
  };

  const handleWeightChange = (v) => {
    setPerItemWeight(v);
    const pw = Number(v);
    if (!Number.isFinite(pw) || pw <= 0) return;
    const q = Number(selectedQuantity) || 1;
    const w = recalculateWeight(q, pw);
    setSelectedWeight(w);
    applyMacrosFromMeasurement(w);
  };

  const handleUnitChange = (unit) => {
    const q = Number(selectedQuantity) || 1;
    const next = recalculateForNewUnit(unit, q, baseUnit);
    setSelectedUnit(next.selectedUnit);
    setPerItemWeight(next.perItemWeight);
    setSelectedWeight(next.selectedWeight);
    applyMacrosFromMeasurement(next.selectedWeight);
  };

  const handleSave = async () => {
    if (!plan?.id) return;
    if (!String(name || "").trim()) return toast.error("Name is required");
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }
    const q = Number(selectedQuantity);
    const pw = Number(perItemWeight);
    const supplementSave = isMealEdit && itemType === "supplement";
    if (!supplementSave) {
      if (!Number.isFinite(q) || q <= 0)
        return toast.error("Enter a valid quantity");
      if (!Number.isFinite(pw) || pw <= 0)
        return toast.error("Enter a valid weight per item");
    }

    setSaving(true);
    try {
      const weight = Number(selectedWeight) || q * pw;
      const payload = {
        from_date: fromDate || undefined,
        till_date: tillDate || undefined,
        assignment_mode: assignmentMode,
        weekdays:
          assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
        status: planStatus,
        client_note: String(clientNote || "").trim() || null,
        food: supplementSave
          ? {
              name: String(name || "").trim(),
              description: String(description || "").trim() || null,
              calories: 0,
              fat: 0,
              carbs: 0,
              protein: 0,
            }
          : {
              name,
              description,
              calories: calories === "" ? null : Number(calories),
              fat: fat === "" ? null : Number(fat),
              carbs: carbs === "" ? null : Number(carbs),
              protein: protein === "" ? null : Number(protein),
              ...(hasMicros
                ? { micronutrients: buildMicronutrientsObject(microRows) }
                : {}),
              serving_size: String(q),
              unit: selectedUnit,
              weight,
              weight_unit: baseUnit,
              total_quantity: {
                value: weight,
                unit: selectedUnit,
              },
              calories_per_100g: per100.calories,
              fat_per_100g: per100.fat,
              carbs_per_100g: per100.carbs,
              protein_per_100g: per100.protein,
            },
      };
      if (isMealEdit) {
        payload.date = clientNowDateString();
        payload.item_type = itemType;
        if (supplementSave) {
          payload.dose_per_day = dosePerDay;
          payload.molecular_name =
            String(molecularName || "").trim() || null;
          payload.company_name = String(companyName || "").trim() || null;
        }
        await companyAdminCustomersAPI.mealFoodUpdate(plan.id, payload);
      } else {
        payload.portion = 1;
        await companyAdminCustomersAPI.strictUpdate(plan.id, payload);
      }
      toast.success("Updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Update failed",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  if (isSupplement) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
        <div
          className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}
          data-testid="edit-supplement-modal"
        >
          <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Edit Supplement
              </h3>
              <div className="text-xs text-muted-foreground mt-1">
                No calories or nutrition — not included in diet calculations.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label className="text-foreground">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Vitamin D3"
              />
            </div>
            <div>
              <Label className="text-foreground">Molecular name</Label>
              <Input
                value={molecularName}
                onChange={(e) => setMolecularName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label className="text-foreground">Company name</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label className="text-foreground">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Optional notes"
                className="resize-y"
              />
            </div>
            <div>
              <Label className="text-foreground">Dose</Label>
              <select
                className="form-select mt-1"
                value={dosePerDay}
                onChange={(e) =>
                  setDosePerDay(Number(e.target.value) === 2 ? 2 : 1)
                }
              >
                <option value={1}>1 per day</option>
                <option value={2}>2 per day</option>
              </select>
            </div>
            <div>
              <Label className="text-foreground">Status</Label>
              <select
                className="form-select"
                value={planStatus}
                onChange={(e) => setPlanStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
                <option value={2}>Expired</option>
                <option value={3}>Soft Delete</option>
              </select>
            </div>
            <div>
              <Label className="text-foreground flex items-center gap-1">
                <Info className="w-3.5 h-3.5" />
                Coach note (visible in app)
              </Label>
              <Textarea
                value={clientNote}
                onChange={(e) => setClientNote(e.target.value)}
                placeholder="Optional tip for the client"
                rows={2}
                className="mt-1 resize-y"
              />
            </div>
            <StrictSchedulePicker
              fromDate={fromDate}
              tillDate={tillDate}
              onFromChange={(v) => {
                setFromDate(v);
                if (v) {
                  const d = new Date(`${v}T00:00:00`);
                  setTillDate(isoDate(addDays(d, 7)));
                }
              }}
              onTillChange={setTillDate}
              assignmentMode={assignmentMode}
              weekdays={weekdays}
              onModeChange={setAssignmentMode}
              onWeekdaysChange={setWeekdays}
            />
          </div>
          <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} role="presentation">
      <div className="absolute inset-0 flex items-center justify-center p-0 sm:p-6">
        <div className="panel w-full h-full sm:h-auto sm:max-h-[75vh] app-modal-shell sm:max-w-6xl p-0 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Edit Food
              </h3>
              <div className="text-xs text-muted-foreground mt-1">
                Update nutrition and serving information for this added food.
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>

          <div className="app-modal-body p-6 overflow-auto flex-1 min-h-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 space-y-6">
                <div className="rounded-md border border-border p-4">
                  <div className="text-sm font-semibold text-foreground">
                    Macros
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Auto-updates when Weight / Quantity / Unit change (same as
                    mobile). You can still override values manually.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <NumberField
                      label="Calories"
                      value={calories}
                      onChange={setCalories}
                      placeholder="kcal"
                    />
                    <NumberField
                      label="Fat"
                      value={fat}
                      onChange={setFat}
                      placeholder="g"
                    />
                    <NumberField
                      label="Carbs"
                      value={carbs}
                      onChange={setCarbs}
                      placeholder="g"
                    />
                    <NumberField
                      label="Protein"
                      value={protein}
                      onChange={setProtein}
                      placeholder="g"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-border p-4">
                  <div className="text-sm font-semibold text-foreground">
                    General
                  </div>
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <div>
                      <Label className="text-foreground">Food Name</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Food name"
                      />
                    </div>
                    <div>
                      <Label className="text-foreground flex items-center gap-1">
                        <Info className="w-3.5 h-3.5" />
                        Coach note (visible in app)
                      </Label>
                      <Textarea
                        value={clientNote}
                        onChange={(e) => setClientNote(e.target.value)}
                        placeholder="e.g. Eat this 30 min before workout"
                        rows={2}
                        className="mt-1 resize-y"
                        data-testid="edit-food-client-note"
                      />
                    </div>
                    <div>
                      <Label className="text-foreground">Status</Label>
                      <select
                        className="form-select"
                        value={planStatus}
                        onChange={(e) => setPlanStatus(Number(e.target.value))}
                      >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                        <option value={2}>Expired</option>
                        <option value={3}>Soft Delete</option>
                      </select>
                      <div className="text-xs text-muted-foreground mt-1">
                        0=Inactive, 1=Active, 2=Expired, 3=Soft Delete (hidden
                        in list)
                      </div>
                    </div>
                    <div>
                      <Label className="text-foreground">Description</Label>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Food description / ingredients"
                      />
                      <div className="text-xs text-muted-foreground mt-1">
                        Food details only — not the coach tip shown in the app.
                      </div>
                    </div>
                  </div>
                </div>

                <IntakeMeasurementCard
                  selectedQuantity={selectedQuantity}
                  perItemWeight={perItemWeight}
                  selectedUnit={selectedUnit}
                  baseUnit={baseUnit}
                  onQuantityChange={handleQuantityChange}
                  onWeightChange={handleWeightChange}
                  onUnitChange={handleUnitChange}
                />
              </div>

              <div className="lg:col-span-5 space-y-6">
                {hasMicros && (
                  <div className="rounded-md border border-border p-4">
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        Micronutrients
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Edit micronutrients values. Scroll to view
                        vitamins/minerals.
                      </div>
                    </div>

                    <div className="mt-4 rounded-md border border-border overflow-hidden h-[320px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted">
                            <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                              Category
                            </TableHead>
                            <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                              Nutrient
                            </TableHead>
                            <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                              Value
                            </TableHead>
                            <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                              Unit
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {microRows.map((r, idx) => (
                            <TableRow
                              key={`${r.category}-${r.nutrient}-${idx}`}
                              className="hover:bg-muted"
                            >
                              <TableCell className="text-muted-foreground">
                                {r.category || "-"}
                              </TableCell>
                              <TableCell className="font-medium text-foreground">
                                {r.nutrient}
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={r.value}
                                  onChange={(e) => {
                                    const next = [...microRows];
                                    next[idx] = {
                                      ...next[idx],
                                      value: e.target.value,
                                    };
                                    setMicroRows(next);
                                  }}
                                />
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {r.unit || "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <StrictSchedulePicker
                  fromDate={fromDate}
                  tillDate={tillDate}
                  onFromChange={setFromDate}
                  onTillChange={setTillDate}
                  assignmentMode={assignmentMode}
                  weekdays={weekdays}
                  onModeChange={setAssignmentMode}
                  onWeekdaysChange={setWeekdays}
                />
              </div>
            </div>
          </div>

          <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** WingsDiet `/foodbank/detail` — aligns with `nutrition.macronutrient` in strict create payload */
function readFoodbankDetailMacros(detail) {
  if (!detail || typeof detail !== "object") return null;
  const macro = detail.nutrition?.macronutrient || detail.macronutrient || {};
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  return {
    calories: n(
      macro["energy-kcal"] ??
        macro.energy_kcal ??
        macro.energyKcal ??
        macro.calories,
    ),
    protein: n(macro.proteins ?? macro.protein),
    fat: n(macro.fat),
    carbs: n(macro.carbohydrates ?? macro.carbs),
  };
}

function PortionNutritionPreview({
  portion,
  calories,
  protein,
  fat,
  carbs,
  subtitle,
}) {
  const p = Number(portion) || 1;
  const scale = (v) => Math.round((Number(v) || 0) * p * 10) / 10;
  const rows = [
    ["Calories", scale(calories), "kcal"],
    ["Protein", scale(protein), "g"],
    ["Fat", scale(fat), "g"],
    ["Carbs", scale(carbs), "g"],
  ];
  return (
    <div className="rounded-md border border-border bg-muted px-4 py-3 text-xs space-y-1.5">
      <div className="font-semibold text-foreground text-sm mb-1">
        Nutrition for {p} portion{p !== 1 ? "s" : ""}
        {subtitle ? (
          <span className="font-normal text-muted-foreground"> {subtitle}</span>
        ) : null}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {rows.map(([label, val, unit]) => (
          <div
            key={label}
            className="bg-card rounded border border-border py-1.5 px-1"
          >
            <div className="font-semibold text-foreground">
              {val}
              {unit}
            </div>
            <div className="text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mobile-style Intake Measurement card (Weight / Quantity / Unit). */
function IntakeMeasurementCard({
  selectedQuantity,
  perItemWeight,
  selectedUnit,
  baseUnit,
  onQuantityChange,
  onWeightChange,
  onUnitChange,
}) {
  const baseLabel =
    String(baseUnit || "g").toLowerCase() === "ml" ? "ml" : "g";
  const measurements = measurementsForBase(baseUnit);

  return (
    <div
      className="rounded-md border border-border bg-primary/5 px-4 py-3 space-y-3"
      data-testid="intake-measurement-card"
    >
      <div className="text-sm font-semibold text-foreground">
        Intake Measurement
      </div>
      <div>
        <Label className="text-foreground">Weight (per item)</Label>
        <div className="mt-1 flex gap-2">
          <Input
            type="number"
            min="0.1"
            step="0.1"
            value={perItemWeight ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              onWeightChange(v);
            }}
            data-testid="intake-weight"
          />
          <span className="flex items-center text-sm text-muted-foreground min-w-[2rem]">
            {baseLabel}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-foreground">Quantity</Label>
          <Input
            type="number"
            min="0.1"
            step="0.1"
            className="mt-1"
            value={selectedQuantity ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              onQuantityChange(v);
            }}
            data-testid="intake-quantity"
          />
        </div>
        <div>
          <Label className="text-foreground">Unit</Label>
          <select
            className="form-select mt-1"
            value={selectedUnit}
            onChange={(e) => onUnitChange(e.target.value)}
            data-testid="intake-unit"
          >
            {/* Keep current unit visible even if not in list */}
            {!measurements.some(
              (m) => m.toLowerCase() === String(selectedUnit).toLowerCase(),
            ) && selectedUnit ? (
              <option value={selectedUnit}>{selectedUnit}</option>
            ) : null}
            {measurements.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        Total weight:{" "}
        <span className="font-semibold text-foreground">
          {Number(selectedQuantity || 0) * Number(perItemWeight || 0) > 0
            ? (
                Number(selectedQuantity || 0) * Number(perItemWeight || 0)
              ).toFixed(1)
            : "—"}{" "}
          {baseLabel}
        </span>
        {" · "}Changing unit updates weight via standard conversion (same as
        mobile).
      </div>
    </div>
  );
}

const AddFoodModal = ({
  open,
  onClose,
  userId,
  mealTimeId,
  mealTimes = [],
  onCreated,
  enabled,
  planRole = "assigned",
  /** "strict" = StrictPlanDiet; "meal" = customer Food for meal (moderate/flex) */
  createMode = "strict",
}) => {
  const isMealMode = createMode === "meal";
  const [saving, setSaving] = useState(false);
  const [foodSource, setFoodSource] = useState("food_bank");
  const [foodQuery, setFoodQuery] = useState("");
  const [selectedFood, setSelectedFood] = useState(null);
  /** Full food detail from detail API (food_bank only); used to create Food + strict plan */
  const [detailedFood, setDetailedFood] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientNote, setClientNote] = useState("");
  const [pickedMealTimeId, setPickedMealTimeId] = useState(mealTimeId || "");

  // Mobile-style measurement (Food Bank + USDA)
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [perItemWeight, setPerItemWeight] = useState(100);
  const [selectedWeight, setSelectedWeight] = useState(100);
  const [selectedUnit, setSelectedUnit] = useState("Gram");
  const [baseUnit, setBaseUnit] = useState("g");
  const [per100, setPer100] = useState({
    calories: 0,
    fat: 0,
    protein: 0,
    carbs: 0,
  });

  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const effectiveMealTimeId = mealTimeId || pickedMealTimeId;
  const showIntakeMeasurement =
    !!selectedFood && !!detailedFood && !detailLoading;

  const measuredMacros = useMemo(() => {
    const w = Number(selectedWeight) || 0;
    return {
      calories: calcMacroFromPer100(per100.calories, w),
      fat: calcMacroFromPer100(per100.fat, w),
      protein: calcMacroFromPer100(per100.protein, w),
      carbs: calcMacroFromPer100(per100.carbs, w),
    };
  }, [per100, selectedWeight]);

  const resetMeasurement = () => {
    setSelectedQuantity(1);
    setPerItemWeight(100);
    setSelectedWeight(100);
    setSelectedUnit("Gram");
    setBaseUnit("g");
    setPer100({ calories: 0, fat: 0, protein: 0, carbs: 0 });
  };

  const applyMeasurementFromDetail = (detail) => {
    const init = initMeasurementFromFoodDetail(detail);
    setPer100(init.per100);
    setSelectedQuantity(init.selectedQuantity);
    setPerItemWeight(init.perItemWeight);
    setSelectedWeight(init.selectedWeight);
    setBaseUnit(init.baseUnit);
    // Prefer display label from measurement list when possible
    const list = measurementsForBase(init.baseUnit);
    const match = list.find(
      (m) => m.toLowerCase() === String(init.selectedUnit).toLowerCase(),
    );
    setSelectedUnit(match || init.selectedUnit || list[0]);
  };

  /** Build food-bank-shaped detail from USDA search row (macros are per 100g). */
  const buildUsdaDetailFromSearchItem = (item) => ({
    product_name: item.product_name,
    description: null,
    nutrition: {
      macronutrient: {
        "energy-kcal": Number(item.calories) || 0,
        fat: Number(item.fat) || 0,
        carbohydrates: Number(item.carbs) || 0,
        proteins: Number(item.protein) || 0,
      },
      micronutrient: item.nutrients || {},
    },
    serving: {
      serving_quantity: 100,
      serving_quantity_unit: "g",
      serving_size: item.serving || "100g",
    },
    food_bank: "usda",
    source: "usda",
  });

  useEffect(() => {
    if (open) {
      setFoodSource("food_bank");
      setFoodQuery("");
      setSelectedFood(null);
      setDetailedFood(null);
      setClientNote("");
      setPickedMealTimeId(mealTimeId || mealTimes[0]?.id || "");
      setFromDate(isoDate(today));
      setTillDate(isoDate(addDays(today, 7)));
      setAssignmentMode("weekly");
      setWeekdays([...ALL_WEEKDAY_VALUES]);
      resetMeasurement();
    }
  }, [open, today]);

  const handleSelectFood = async (item) => {
    setSelectedFood(item);
    setFoodQuery(item.product_name);
    setDetailedFood(null);
    resetMeasurement();
    if (foodSource === "usda") {
      const detail = buildUsdaDetailFromSearchItem(item);
      setDetailedFood(detail);
      applyMeasurementFromDetail(detail);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await companyAdminCustomersAPI.foodbankDetail({
        id: item.id,
        food_bank: item.food_bank ?? "db1",
      });
      setDetailedFood(res.data);
      applyMeasurementFromDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load food details");
      setSelectedFood(null);
      setFoodQuery("");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleQuantityChange = (v) => {
    setSelectedQuantity(v);
    const q = Number(v);
    if (!Number.isFinite(q) || q <= 0) return;
    setSelectedWeight(recalculateWeight(q, perItemWeight));
  };

  const handleWeightChange = (v) => {
    setPerItemWeight(v);
    const w = Number(v);
    if (!Number.isFinite(w) || w <= 0) return;
    const q = Number(selectedQuantity) || 1;
    setSelectedWeight(recalculateWeight(q, w));
  };

  const handleUnitChange = (unit) => {
    const q = Number(selectedQuantity) || 1;
    const next = recalculateForNewUnit(unit, q, baseUnit);
    setSelectedUnit(next.selectedUnit);
    setPerItemWeight(next.perItemWeight);
    setSelectedWeight(next.selectedWeight);
  };

  const buildMeasuredFoodDetail = (detail) => {
    const qty = Number(selectedQuantity) || 1;
    const weight = Number(selectedWeight) || 0;
    const perItem = Number(perItemWeight) || weight;
    return {
      ...detail,
      serving: {
        ...(detail?.serving || {}),
        serving_quantity: perItem,
        serving_quantity_unit: baseUnit,
        serving_size: String(qty),
      },
      measured: {
        quantity: qty,
        serving_size: String(qty),
        unit: selectedUnit,
        weight,
        weight_unit: baseUnit,
        base_unit: baseUnit,
        calories: measuredMacros.calories,
        fat: measuredMacros.fat,
        carbs: measuredMacros.carbs,
        proteins: measuredMacros.protein,
        calories_per_100g: per100.calories,
        fat_per_100g: per100.fat,
        carbs_per_100g: per100.carbs,
        protein_per_100g: per100.protein,
      },
    };
  };

  const handleSubmit = async () => {
    if (!enabled) return toast.error("Food add is not available right now");
    if (!selectedFood) return toast.error("Please select a food");
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    if (!detailedFood)
      return toast.error(
        "Please wait for food details to load or select again",
      );
    {
      const q = Number(selectedQuantity);
      const w = Number(perItemWeight);
      if (!Number.isFinite(q) || q <= 0)
        return toast.error("Enter a valid quantity");
      if (!Number.isFinite(w) || w <= 0)
        return toast.error("Enter a valid weight per item");
    }
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }

    setSaving(true);
    try {
      // Food Bank + USDA: macros already include quantity × weight
      const foodDetailPayload = buildMeasuredFoodDetail(detailedFood);
      const planPortion = 1;

      if (isMealMode) {
        const payload = {
          user_id: userId,
          meal_time_id: effectiveMealTimeId,
          food_name: selectedFood.product_name,
          food_bank: selectedFood.food_bank,
          external_id: selectedFood.id,
          client_note: String(clientNote || "").trim() || null,
          date: clientNowDateString(),
          from_date: fromDate,
          till_date: tillDate,
          assignment_mode: assignmentMode,
          weekdays:
            assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
          item_type: "food",
        };
        if (foodDetailPayload) payload.food_detail = foodDetailPayload;
        const res = await companyAdminCustomersAPI.mealFoodCreate(payload);
        toast.success("Food added");
        onCreated?.(res.data);
        onClose();
        return;
      }

      const payload = {
        user_id: userId,
        meal_time_id: effectiveMealTimeId,
        food_source: foodSource,
        food_name: selectedFood.product_name,
        food_bank: selectedFood.food_bank,
        external_id: selectedFood.id,
        portion: planPortion,
        from_date: fromDate,
        till_date: tillDate,
        assignment_mode: assignmentMode,
        weekdays:
          assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
        plan_role: "assigned",
        client_note: String(clientNote || "").trim() || null,
      };
      if (foodDetailPayload) payload.food_detail = foodDetailPayload;
      const res = await companyAdminCustomersAPI.strictCreate(payload);
      toast.success("Food added");
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to add food",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}
        data-testid="add-food-modal"
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">Add Food</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 flex-1 min-h-0 overflow-y-auto">
          {!enabled && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground">
              Foods are not available for this customer right now.
            </div>
          )}

          {!mealTimeId && (
            <div>
              <Label className="text-foreground">Meal time</Label>
              <select
                className="form-select"
                value={pickedMealTimeId}
                onChange={(e) => setPickedMealTimeId(e.target.value)}
                disabled={!enabled}
              >
                <option value="">Select meal time</option>
                {mealTimes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.meal_type || m.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-foreground">Select Food Source</Label>
            <select
              className="form-select"
              value={foodSource}
              onChange={(e) => {
                setFoodSource(e.target.value);
                setFoodQuery("");
                setSelectedFood(null);
                setDetailedFood(null);
                resetMeasurement();
              }}
              data-testid="food-source-select"
            >
              <option value="food_bank">Food Bank</option>
              <option value="usda">USDA Food Data</option>
            </select>
          </div>

          <div>
            <Label className="text-foreground">Food Name</Label>
            <FoodSearch
              value={foodQuery}
              selectedFood={selectedFood}
              source={foodSource}
              onChange={(v) => {
                setFoodQuery(v);
                setSelectedFood(null);
                setDetailedFood(null);
                resetMeasurement();
              }}
              onSelect={handleSelectFood}
            />
            {detailLoading && (
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading food details…
              </div>
            )}
            {selectedFood && !detailLoading && (
              <div className="text-xs text-muted-foreground mt-2">
                Selected:{" "}
                <span className="text-foreground font-semibold">
                  {selectedFood.product_name}
                </span>
                {detailedFood && (
                  <span className="text-emerald-600 ml-1">
                    • Details loaded
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Food Bank + USDA: mobile-style Intake Measurement */}
          {showIntakeMeasurement && (
              <IntakeMeasurementCard
                selectedQuantity={selectedQuantity}
                perItemWeight={perItemWeight}
                selectedUnit={selectedUnit}
                baseUnit={baseUnit}
                onQuantityChange={handleQuantityChange}
                onWeightChange={handleWeightChange}
                onUnitChange={handleUnitChange}
              />
            )}

          <div>
            <Label className="text-foreground flex items-center gap-1">
              <Info className="w-3.5 h-3.5" />
              Coach note (visible in app)
            </Label>
            <Textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              rows={2}
              placeholder="e.g. Eat this 30 min before workout"
              disabled={!enabled}
              className="mt-1 resize-y"
              data-testid="food-client-note"
            />
            <div className="text-xs text-muted-foreground mt-1">
              {isMealMode
                ? "Saved on the food for the customer’s meal list."
                : "Separate from food description — tip on this food in the app."}
            </div>
          </div>

          {/* Nutrition preview — scales with quantity × weight for both sources */}
          {showIntakeMeasurement && (
              <PortionNutritionPreview
                portion={1}
                calories={measuredMacros.calories}
                protein={measuredMacros.protein}
                fat={measuredMacros.fat}
                carbs={measuredMacros.carbs}
                subtitle={`(for ${Number(selectedWeight || 0).toFixed(1)} ${
                  String(baseUnit).toLowerCase() === "ml" ? "ml" : "g"
                })`}
              />
            )}

          <StrictSchedulePicker
            fromDate={fromDate}
            tillDate={tillDate}
            onFromChange={(v) => {
              setFromDate(v);
              if (v) {
                const d = new Date(v + "T00:00:00");
                setTillDate(isoDate(addDays(d, 7)));
              }
            }}
            onTillChange={setTillDate}
            assignmentMode={assignmentMode}
            weekdays={weekdays}
            onModeChange={setAssignmentMode}
            onWeekdaysChange={setWeekdays}
          />
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              saving ||
              detailLoading ||
              !selectedFood ||
              !detailedFood
            }
            data-testid="add-food-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Dedicated supplement form — no food bank / nutrition (not used in calorie calculations). */
const AddSupplementModal = ({
  open,
  onClose,
  userId,
  mealTimeId,
  mealTimes = [],
  onCreated,
  enabled,
}) => {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [molecularName, setMolecularName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [description, setDescription] = useState("");
  const [dosePerDay, setDosePerDay] = useState(1);
  const [pickedMealTimeId, setPickedMealTimeId] = useState(mealTimeId || "");
  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const effectiveMealTimeId = mealTimeId || pickedMealTimeId;

  useEffect(() => {
    if (open) {
      setName("");
      setMolecularName("");
      setCompanyName("");
      setDescription("");
      setDosePerDay(1);
      setPickedMealTimeId(mealTimeId || mealTimes[0]?.id || "");
      setFromDate(isoDate(today));
      setTillDate(isoDate(addDays(today, 7)));
      setAssignmentMode("weekly");
      setWeekdays([...ALL_WEEKDAY_VALUES]);
    }
  }, [open, today, mealTimeId, mealTimes]);

  const handleSubmit = async () => {
    if (!enabled) return toast.error("Not available right now");
    const trimmed = String(name || "").trim();
    if (!trimmed) return toast.error("Name is required");
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    if (!fromDate || !tillDate) return toast.error("Please select duration");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }
    setSaving(true);
    try {
      const res = await companyAdminCustomersAPI.mealFoodCreate({
        user_id: userId,
        meal_time_id: effectiveMealTimeId,
        item_type: "supplement",
        name: trimmed,
        food_name: trimmed,
        molecular_name: String(molecularName || "").trim() || null,
        company_name: String(companyName || "").trim() || null,
        description: String(description || "").trim() || null,
        dose_per_day: dosePerDay,
        date: clientNowDateString(),
        from_date: fromDate,
        till_date: tillDate,
        assignment_mode: assignmentMode,
        weekdays:
          assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
      });
      toast.success("Supplement added");
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to add supplement",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="add-supplement-modal"
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Add Supplement
            </h3>
            <div className="text-xs text-muted-foreground mt-1">
              No calories or nutrition — not included in diet calculations.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {!enabled && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground">
              Not available for this customer right now.
            </div>
          )}

          {!mealTimeId && (
            <div>
              <Label className="text-foreground">Meal time</Label>
              <select
                className="form-select"
                value={pickedMealTimeId}
                onChange={(e) => setPickedMealTimeId(e.target.value)}
                disabled={!enabled}
              >
                <option value="">Select meal time</option>
                {mealTimes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.meal_type || m.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-foreground">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vitamin D3"
              disabled={!enabled}
            />
          </div>
          <div>
            <Label className="text-foreground">Molecular name</Label>
            <Input
              value={molecularName}
              onChange={(e) => setMolecularName(e.target.value)}
              placeholder="Optional"
              disabled={!enabled}
            />
          </div>
          <div>
            <Label className="text-foreground">Company name</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Optional"
              disabled={!enabled}
            />
          </div>
          <div>
            <Label className="text-foreground">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional notes"
              disabled={!enabled}
              className="resize-y"
            />
          </div>
          <div>
            <Label className="text-foreground">Dose</Label>
            <select
              className="form-select mt-1"
              value={dosePerDay}
              onChange={(e) =>
                setDosePerDay(Number(e.target.value) === 2 ? 2 : 1)
              }
              disabled={!enabled}
            >
              <option value={1}>1 per day</option>
              <option value={2}>2 per day</option>
            </select>
          </div>

          <StrictSchedulePicker
            fromDate={fromDate}
            tillDate={tillDate}
            onFromChange={(v) => {
              setFromDate(v);
              if (v) {
                const d = new Date(`${v}T00:00:00`);
                setTillDate(isoDate(addDays(d, 7)));
              }
            }}
            onTillChange={setTillDate}
            assignmentMode={assignmentMode}
            weekdays={weekdays}
            onModeChange={setAssignmentMode}
            onWeekdaysChange={setWeekdays}
          />
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !enabled || !String(name || "").trim()}
            data-testid="add-supplement-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Strict plan: name + description → server runs same AI nutrition as mobile /mobile/ai/nutrition, then creates Food + strict row. */
const AddFoodByAiModal = ({
  open,
  onClose,
  userId,
  mealTimeId,
  mealTimes = [],
  onCreated,
  enabled,
  planRole = "assigned",
  createMode = "strict",
}) => {
  const isMealMode = createMode === "meal";
  const [saving, setSaving] = useState(false);
  const [foodName, setFoodName] = useState("");
  const [foodDescription, setFoodDescription] = useState("");
  const [portion, setPortion] = useState(1);
  const [clientNote, setClientNote] = useState("");
  const [pickedMealTimeId, setPickedMealTimeId] = useState(mealTimeId || "");

  /** "custom" = type a food name/description; "recipe" = seed from a saved recipe. */
  const [foodSource, setFoodSource] = useState("custom");
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [loadingSavedRecipes, setLoadingSavedRecipes] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");

  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const effectiveMealTimeId = mealTimeId || pickedMealTimeId;

  useEffect(() => {
    if (open) {
      setFoodName("");
      setFoodDescription("");
      setPortion(1);
      setClientNote("");
      setPickedMealTimeId(mealTimeId || mealTimes[0]?.id || "");
      setFromDate(isoDate(today));
      setTillDate(isoDate(addDays(today, 7)));
      setAssignmentMode("weekly");
      setWeekdays([...ALL_WEEKDAY_VALUES]);
      setFoodSource("custom");
      setSelectedRecipeId("");

      setLoadingSavedRecipes(true);
      companyAdminRecipesAPI
        .getAll({ limit: 100 })
        .then((res) => setSavedRecipes(res.data.recipes || []))
        .catch(() => setSavedRecipes([]))
        .finally(() => setLoadingSavedRecipes(false));
    }
  }, [open, today, mealTimeId, mealTimes]);

  const handleSelectSavedRecipe = (recipeId) => {
    setSelectedRecipeId(recipeId);
    const recipe = savedRecipes.find((r) => r._id === recipeId);
    if (!recipe) return;
    setFoodName(recipe.name || "");
    const lines = [
      `Homemade recipe: ${recipe.name || ""}`,
      `Yields ${recipe.servings || 1} serving(s). Describe ONE serving.`,
    ];
    if (recipe.serving_suggestion) {
      lines.push(`One serving looks like: ${recipe.serving_suggestion}`);
    }
    if ((recipe.ingredients || []).length) {
      lines.push("Ingredients for the full recipe:");
      recipe.ingredients.forEach((ing) => lines.push(`- ${ing}`));
    }
    if (recipe.instructions) {
      lines.push(`Preparation: ${recipe.instructions}`);
    }
    setFoodDescription(lines.join("\n"));
  };

  const handleSubmit = async () => {
    if (!enabled) {
      return toast.error("Food add is not available right now");
    }
    const name = String(foodName || "").trim();
    const desc = String(foodDescription || "").trim();
    if (!name) return toast.error("Please enter a food name");
    if (!desc) {
      return toast.error(
        "Please enter a food description (size, measurements, units, how it is prepared)",
      );
    }
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }

    setSaving(true);
    try {
      if (isMealMode) {
        const res = await companyAdminCustomersAPI.mealFoodCreateFromAi({
          user_id: userId,
          meal_time_id: effectiveMealTimeId,
          food_name: name,
          food_description: desc,
          client_note: String(clientNote || "").trim() || null,
          date: clientNowDateString(),
          from_date: fromDate,
          till_date: tillDate,
          assignment_mode: assignmentMode,
          weekdays:
            assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
          item_type: "food",
        });
        toast.success("Food added from AI");
        onCreated?.(res.data);
        onClose();
        return;
      }
      const res = await companyAdminCustomersAPI.strictCreateFromAi({
        user_id: userId,
        meal_time_id: effectiveMealTimeId,
        food_name: name,
        food_description: desc,
        client_note: String(clientNote || "").trim() || null,
        portion: normalizePortionInput(portion),
        from_date: fromDate,
        till_date: tillDate,
        assignment_mode: assignmentMode,
        weekdays:
          assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
        plan_role: "assigned",
      });
      toast.success("Food added from AI estimate");
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to generate or save food",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-xl mx-0 p-0 app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}
        data-testid="add-food-by-ai-modal"
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Add Food by AI
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4">
          {!enabled && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground">
              Foods are not available for this customer right now.
            </div>
          )}

          <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            Nutrition is estimated with the same AI pipeline as mobile food
            logging. You must give a clear description with size and
            measurements so the estimate is accurate.
            {planRole === "recommendation"
              ? " A new food is added to this meal for the customer to log."
              : " A new food and strict plan entry are created when you submit."}
          </div>

          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setFoodSource("custom")}
              disabled={!enabled || saving}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
                foodSource === "custom"
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
              data-testid="ai-food-source-custom"
            >
              <Sparkles className="w-4 h-4" />
              Custom
            </button>
            <button
              type="button"
              onClick={() => setFoodSource("recipe")}
              disabled={!enabled || saving}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
                foodSource === "recipe"
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
              data-testid="ai-food-source-recipe"
            >
              <BookOpen className="w-4 h-4" />
              From Recipe
            </button>
          </div>

          {foodSource === "recipe" && (
            <div>
              <Label className="text-foreground">Saved recipe</Label>
              <select
                className="form-select"
                value={selectedRecipeId}
                onChange={(e) => handleSelectSavedRecipe(e.target.value)}
                disabled={!enabled || loadingSavedRecipes}
                data-testid="ai-food-recipe-picker"
              >
                <option value="">
                  {loadingSavedRecipes
                    ? "Loading recipes…"
                    : savedRecipes.length === 0
                      ? "No saved recipes yet"
                      : "Select a recipe"}
                </option>
                {savedRecipes.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-muted-foreground mt-1">
                Fills in name and description below — edit before generating
                if needed.
              </div>
            </div>
          )}

          {!mealTimeId && (
            <div>
              <Label className="text-foreground">Meal time</Label>
              <select
                className="form-select"
                value={pickedMealTimeId}
                onChange={(e) => setPickedMealTimeId(e.target.value)}
                disabled={!enabled}
              >
                <option value="">Select meal time</option>
                {mealTimes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.meal_type || m.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-foreground">Food name</Label>
            <Input
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
              placeholder="e.g. Grilled chicken breast"
              disabled={!enabled}
            />
          </div>

          <div>
            <Label className="text-foreground">
              Food description (required)
            </Label>
            <Textarea
              value={foodDescription}
              onChange={(e) => setFoodDescription(e.target.value)}
              placeholder="e.g. 150 g cooked weight, palm-sized piece, no skin; with 1 cup steamed rice (200 g)"
              rows={3}
              disabled={!enabled}
              className="resize-y min-h-[80px]"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Used for AI nutrition estimate only — not shown as the coach tip.
            </div>
          </div>

          <div>
            <Label className="text-foreground flex items-center gap-1">
              <Info className="w-3.5 h-3.5" />
              Coach note (visible in app)
            </Label>
            <Textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              rows={2}
              placeholder="e.g. Eat this 30 min before workout"
              disabled={!enabled}
              className="mt-1 resize-y"
              data-testid="food-client-note-ai"
            />
          </div>

          <div>
            <Label className="text-foreground">Portion</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={portion}
              onChange={(e) =>
                setPortion(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="1"
              disabled={!enabled}
            />
          </div>

          <StrictSchedulePicker
            fromDate={fromDate}
            tillDate={tillDate}
            onFromChange={(v) => {
              setFromDate(v);
              if (v) {
                const d = new Date(`${v}T00:00:00`);
                setTillDate(isoDate(addDays(d, 7)));
              }
            }}
            onTillChange={setTillDate}
            assignmentMode={assignmentMode}
            weekdays={weekdays}
            onModeChange={setAssignmentMode}
            onWeekdaysChange={setWeekdays}
          />
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              saving ||
              !enabled ||
              !foodName.trim() ||
              !String(foodDescription).trim()
            }
            data-testid="add-food-by-ai-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating & saving…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate & add
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AI_PLAN_PREF_OPTIONS = [
  { id: "local", label: "Local foods" },
  { id: "high_protein", label: "High protein" },
  { id: "vegetarian", label: "Vegetarian" },
  { id: "low_carb", label: "Low carb" },
  { id: "budget", label: "Budget friendly" },
  { id: "easy", label: "Easy to cook" },
];

function formatAiPlanValue(value, fallback = "Not set") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function isAiPlanValueSet(value) {
  const text = String(value ?? "").trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower !== "not set" &&
    lower !== "not available" &&
    lower !== "none" &&
    lower !== "null" &&
    lower !== "undefined"
  );
}

function computeCustomerBmi(customer) {
  const survey = customer?.survey || {};
  const heightCm = Number(survey.height_cm);
  const unit = String(survey.weight_unit || "kg").toLowerCase();
  const weightRaw =
    unit === "lbs" ? Number(survey.weight_lbs) : Number(survey.weight_kg);
  const weightKg = unit === "lbs" ? weightRaw * 0.45359237 : weightRaw;
  if (
    !Number.isFinite(heightCm) ||
    heightCm <= 0 ||
    !Number.isFinite(weightKg) ||
    weightKg <= 0
  ) {
    return null;
  }
  return Math.round((weightKg / Math.pow(heightCm / 100, 2)) * 10) / 10;
}

const GenerateAiPlanModal = ({
  open,
  onClose,
  userId,
  customer = null,
  mealTimeId,
  mealTimes = [],
  onCreated,
  enabled,
  createMode = "meal",
  planType = 3,
}) => {
  const isMealMode = createMode === "meal";
  const [step, setStep] = useState("setup"); // setup | pick | create
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coachNotes, setCoachNotes] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [portion, setPortion] = useState(1);
  const [count, setCount] = useState(5);
  const [prefs, setPrefs] = useState([]);
  const [pickedMealTimeId, setPickedMealTimeId] = useState(mealTimeId || "");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [aiMeta, setAiMeta] = useState(null);
  const [profileUsed, setProfileUsed] = useState(null);

  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const effectiveMealTimeId = mealTimeId || pickedMealTimeId;

  const mealLabel = useMemo(() => {
    const m = (mealTimes || []).find(
      (x) => String(x.id) === String(effectiveMealTimeId),
    );
    return m?.meal_type || m?.id || "";
  }, [mealTimes, effectiveMealTimeId]);

  const profileCards = useMemo(() => {
    const survey = customer?.survey || {};
    const bmi =
      profileUsed?.bmi != null
        ? profileUsed.bmi
        : computeCustomerBmi(customer);
    const resolved =
      profileUsed?.country
        ? {
            country: profileUsed.country,
            timezone: profileUsed.timezone || customer?.timezone || null,
            source: profileUsed.location_source || "country",
          }
        : resolveCustomerLocation(customer);
    const locationRaw = resolved.country || "";
    const locationHint =
      resolved.source === "timezone" && resolved.timezone
        ? `From timezone: ${resolved.timezone}`
        : formatAiPlanValue(customer?.city || profileUsed?.city, "");
    const budgetRaw =
      profileUsed?.weekly_budget ||
      customer?.weekly_budget ||
      customer?.daily_budget ||
      profileUsed?.daily_budget ||
      "";
    const budgetValue = isAiPlanValueSet(budgetRaw) ? budgetRaw : "mid";
    const budgetIsDefault = !isAiPlanValueSet(budgetRaw);
    const goalRaw = profileUsed?.goal || survey.goal || "";
    const targetRaw =
      profileUsed?.target_weight != null && profileUsed?.target_weight !== ""
        ? profileUsed.target_weight
        : survey.target_weight;
    return [
      {
        key: "location",
        label: "Location",
        required: true,
        value: formatAiPlanValue(locationRaw),
        set: isAiPlanValueSet(locationRaw),
        hint: locationHint,
      },
      {
        key: "bmi",
        label: "BMI",
        required: true,
        value: bmi != null ? String(bmi) : "Not available",
        set: bmi != null && Number.isFinite(Number(bmi)),
      },
      {
        key: "goal",
        label: "Goal",
        required: true,
        value: formatAiPlanValue(goalRaw),
        set: isAiPlanValueSet(goalRaw),
      },
      {
        key: "target_weight",
        label: "Target weight",
        required: true,
        value:
          targetRaw != null && targetRaw !== ""
            ? String(targetRaw)
            : "Not set",
        set: targetRaw != null && String(targetRaw).trim() !== "",
      },
      {
        key: "budget",
        label: "Weekly budget",
        required: false,
        value: budgetIsDefault ? "mid (default)" : String(budgetValue),
        set: true,
        hint: budgetIsDefault ? "Used when not set on profile" : "",
      },
      {
        key: "health_issues",
        label: "Health issues",
        required: false,
        value: formatAiPlanValue(
          profileUsed?.health_issues || customer?.health_issues,
          "None",
        ),
        set: true,
      },
      {
        key: "food_allergies",
        label: "Food allergies",
        required: false,
        value: formatAiPlanValue(
          profileUsed?.food_allergies || survey.food_allergies,
          "None",
        ),
        set: true,
      },
      {
        key: "meal_time",
        label: "Meal time",
        required: true,
        value: mealLabel || "Not set",
        set: Boolean(effectiveMealTimeId) && isAiPlanValueSet(mealLabel),
      },
    ];
  }, [customer, mealLabel, profileUsed, effectiveMealTimeId]);

  const missingRequired = useMemo(
    () =>
      profileCards
        .filter((card) => card.required && !card.set)
        .map((card) => card.label),
    [profileCards],
  );
  const canGenerateSuggestions = missingRequired.length === 0;

  useEffect(() => {
    if (open) {
      setStep("setup");
      setCoachNotes("");
      setClientNote("");
      setPortion(1);
      setCount(5);
      setPrefs([]);
      setPickedMealTimeId(mealTimeId || mealTimes[0]?.id || "");
      setSelectedIndex(-1);
      setSuggestions([]);
      setAiMeta(null);
      setProfileUsed(null);
      setFromDate(isoDate(today));
      setTillDate(isoDate(addDays(today, 7)));
      setAssignmentMode("weekly");
      setWeekdays([...ALL_WEEKDAY_VALUES]);
    }
  }, [open, mealTimeId, mealTimes, today]);

  const togglePref = (id) => {
    setPrefs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const buildCoachNotesPayload = () => {
    const prefLabels = AI_PLAN_PREF_OPTIONS.filter((o) => prefs.includes(o.id))
      .map((o) => o.label)
      .join(", ");
    const notes = String(coachNotes || "").trim();
    if (prefLabels && notes) return `Preferences: ${prefLabels}. ${notes}`;
    if (prefLabels) return `Preferences: ${prefLabels}`;
    return notes || undefined;
  };

  const handleGenerate = async () => {
    if (!enabled) return toast.error("Food add is not available right now");
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    if (!canGenerateSuggestions) {
      return toast.error(
        `Complete required customer details first: ${missingRequired.join(", ")}`,
      );
    }
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.dietPlanAiPreview({
        user_id: userId,
        meal_time_id: effectiveMealTimeId,
        diet_plan_type: planType,
        coach_notes: buildCoachNotesPayload(),
        count,
      });
      const next = Array.isArray(res.data?.suggestions)
        ? res.data.suggestions
        : [];
      setSuggestions(next);
      setSelectedIndex(next.length ? 0 : -1);
      setAiMeta(res.data?.ai_meta || null);
      setProfileUsed(res.data?.profile_used || null);
      setStep("pick");
      toast.success("AI meal options are ready");
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to generate AI meal options",
      );
      setSuggestions([]);
      setSelectedIndex(-1);
      setAiMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!enabled) return toast.error("Food add is not available right now");
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }
    const choice = suggestions[selectedIndex];
    if (!choice?.name || !choice?.description) {
      return toast.error("Choose a food first");
    }
    setSaving(true);
    try {
      if (isMealMode) {
        await companyAdminCustomersAPI.mealFoodCreateFromAi({
          user_id: userId,
          meal_time_id: effectiveMealTimeId,
          food_name: choice.name,
          food_description: choice.description,
          client_note: String(clientNote || "").trim() || null,
          date: clientNowDateString(),
          from_date: fromDate,
          till_date: tillDate,
          assignment_mode: assignmentMode,
          weekdays:
            assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
          item_type: "food",
        });
      } else {
        await companyAdminCustomersAPI.strictCreateFromAi({
          user_id: userId,
          meal_time_id: effectiveMealTimeId,
          food_name: choice.name,
          food_description: choice.description,
          client_note: String(clientNote || "").trim() || null,
          portion: normalizePortionInput(portion),
          from_date: fromDate,
          till_date: tillDate,
          assignment_mode: assignmentMode,
          weekdays:
            assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
          plan_role: "assigned",
        });
      }
      toast.success("AI plan food created");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to create selected AI food",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const selectedFood =
    selectedIndex >= 0 ? suggestions[selectedIndex] : null;

  const steps = [
    { id: "setup", label: "1. Inputs" },
    { id: "pick", label: "2. Choose food" },
    { id: "create", label: "3. Create" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-4xl mx-4 p-0 max-h-[75vh] app-modal-shell overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Suggest Foods with AI
            </h3>
            <div className="text-xs text-muted-foreground mt-1">
              {planLabelFromType(planType)} · {mealLabel || "Select meal"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="px-6 pt-4 shrink-0">
          <div className="grid grid-cols-3 gap-2">
            {steps.map((s) => {
              const active = step === s.id;
              const done =
                (s.id === "setup" && (step === "pick" || step === "create")) ||
                (s.id === "pick" && step === "create");
              return (
                <div
                  key={s.id}
                  className={
                    "rounded-md border px-3 py-2 text-xs font-semibold " +
                    (active
                      ? "border-primary bg-primary/10 text-foreground"
                      : done
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-border bg-muted text-muted-foreground")
                  }
                >
                  {s.label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="app-modal-body p-6 overflow-y-auto flex-1 min-h-0 space-y-5">
          {step === "setup" && (
            <>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  What AI will use
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Location uses country when set; otherwise timezone. BMI, goal,
                  target weight, and meal time are required. Weekly budget
                  defaults to mid if not set. Health issues and food allergies
                  are optional.
                </div>
                {!canGenerateSuggestions && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Complete these required fields in the customer profile
                    before generating:{" "}
                    <strong>{missingRequired.join(", ")}</strong>
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {profileCards.map((card) => {
                    const missing = card.required && !card.set;
                    return (
                      <div
                        key={card.key || card.label}
                        className={
                          "rounded-lg border px-3 py-2.5 " +
                          (missing
                            ? "border-amber-300 bg-amber-50"
                            : "border-border bg-card")
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {card.label}
                          </div>
                          <div
                            className={
                              "text-[10px] font-semibold " +
                              (card.required
                                ? missing
                                  ? "text-amber-700"
                                  : "text-emerald-600"
                                : "text-muted-foreground")
                            }
                          >
                            {card.required
                              ? missing
                                ? "Required"
                                : "Ready"
                              : "Optional"}
                          </div>
                        </div>
                        <div
                          className={
                            "text-sm font-semibold mt-1 break-words " +
                            (missing ? "text-amber-800" : "text-foreground")
                          }
                        >
                          {card.value}
                        </div>
                        {card.hint ? (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {card.hint}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted p-4 space-y-4">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Useful options
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Optional preferences to guide the food suggestions.
                  </div>
                </div>

                {!mealTimeId && (
                  <div>
                    <Label className="text-foreground">Meal time</Label>
                    <select
                      className="form-select"
                      value={pickedMealTimeId}
                      onChange={(e) => setPickedMealTimeId(e.target.value)}
                      disabled={!enabled || loading}
                    >
                      <option value="">Select meal time</option>
                      {mealTimes.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.meal_type || m.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <Label className="text-foreground">Food style</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {AI_PLAN_PREF_OPTIONS.map((opt) => {
                      const on = prefs.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => togglePref(opt.id)}
                          disabled={!enabled || loading}
                          className={
                            "h-8 px-3 rounded-full border text-xs font-semibold transition-colors " +
                            (on
                              ? "border-primary bg-primary text-white"
                              : "border-border bg-card text-muted-foreground hover:border-primary/40")
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-foreground">
                      Extra coach guidance
                    </Label>
                    <Textarea
                      value={coachNotes}
                      onChange={(e) => setCoachNotes(e.target.value)}
                      rows={3}
                      placeholder="e.g. prefer Pakistani home food, avoid fried items, keep lunch light"
                      disabled={!enabled || loading}
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">How many options</Label>
                    <select
                      className="form-select"
                      value={count}
                      onChange={(e) => setCount(Number(e.target.value) || 5)}
                      disabled={!enabled || loading}
                    >
                      {[3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          {n} foods
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {step === "pick" && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Choose one food
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Pick the best option for {mealLabel}. You can regenerate if
                    needed.
                  </div>
                </div>
                {aiMeta?.model_used ? (
                  <div className="text-xs text-muted-foreground shrink-0">
                    {aiMeta.model_used}
                  </div>
                ) : null}
              </div>

              {suggestions.length === 0 ? (
                <div className="rounded-md border border-border bg-muted px-4 py-8 text-sm text-muted-foreground text-center">
                  No suggestions yet. Go back and generate options.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {suggestions.map((item, idx) => {
                    const active = idx === selectedIndex;
                    return (
                      <button
                        key={`${item.name}-${idx}`}
                        type="button"
                        onClick={() => setSelectedIndex(idx)}
                        className={
                          "text-left rounded-lg border p-4 transition-colors " +
                          (active
                            ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                            : "border-border bg-card hover:border-primary/40")
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-semibold text-foreground">
                            {item.name}
                          </div>
                          <div
                            className={
                              "mt-0.5 h-4 w-4 rounded-full border shrink-0 " +
                              (active
                                ? "border-primary bg-primary"
                                : "border-[#cbd5e1] bg-card")
                            }
                          />
                        </div>
                        <div className="text-sm text-muted-foreground mt-2">
                          {item.description}
                        </div>
                        {item.serving_hint ? (
                          <div className="text-xs text-muted-foreground mt-2">
                            Serving: {item.serving_hint}
                          </div>
                        ) : null}
                        {item.why_it_fits ? (
                          <div className="text-xs text-muted-foreground mt-2">
                            Why it fits: {item.why_it_fits}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === "create" && selectedFood && (
            <div className="space-y-4">
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Selected food
                </div>
                <div className="text-base font-semibold text-foreground mt-1">
                  {selectedFood.name}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {selectedFood.description}
                </div>
              </div>

              <div>
                <Label className="text-foreground flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" />
                  Coach note (visible in app)
                </Label>
                <Textarea
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                  rows={2}
                  placeholder="Optional note for the customer"
                  disabled={!enabled || saving}
                />
              </div>

              {!isMealMode && (
                <div>
                  <Label className="text-foreground">Portion</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={portion}
                    onChange={(e) =>
                      setPortion(
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    disabled={!enabled || saving}
                  />
                </div>
              )}

              <StrictSchedulePicker
                fromDate={fromDate}
                tillDate={tillDate}
                onFromChange={(v) => {
                  setFromDate(v);
                  if (v) {
                    const d = new Date(`${v}T00:00:00`);
                    setTillDate(isoDate(addDays(d, 7)));
                  }
                }}
                onTillChange={setTillDate}
                assignmentMode={assignmentMode}
                weekdays={weekdays}
                onModeChange={setAssignmentMode}
                onWeekdaysChange={setWeekdays}
              />
            </div>
          )}
        </div>

        <div className="app-modal-footer flex items-center justify-between gap-3 px-6 py-4 border-t border-border shrink-0">
          <div>
            {step !== "setup" ? (
              <Button
                variant="outline"
                onClick={() =>
                  setStep(step === "create" ? "pick" : "setup")
                }
                disabled={loading || saving}
              >
                Back
              </Button>
            ) : (
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === "setup" && (
              <Button
                onClick={handleGenerate}
                disabled={
                  !enabled ||
                  !effectiveMealTimeId ||
                  loading ||
                  !canGenerateSuggestions
                }
                data-testid="diet-generate-ai-plan"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate foods
                  </>
                )}
              </Button>
            )}
            {step === "pick" && (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={
                    !enabled ||
                    loading ||
                    !canGenerateSuggestions
                  }
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Regenerating…
                    </>
                  ) : (
                    "Regenerate"
                  )}
                </Button>
                <Button
                  onClick={() => {
                    if (selectedIndex < 0) {
                      return toast.error("Select a food first");
                    }
                    setStep("create");
                  }}
                  disabled={selectedIndex < 0 || loading}
                >
                  Continue to create
                </Button>
              </>
            )}
            {step === "create" && (
              <Button
                onClick={handleCreate}
                disabled={saving || selectedIndex < 0}
                data-testid="diet-generate-ai-plan-create"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create food
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Strict plan: recipe name → AI recipe details → review → AI nutrition → Food + strict row.
 */
const AddFoodByRecipeModal = ({
  open,
  onClose,
  userId,
  mealTimeId,
  mealTimes = [],
  onCreated,
  enabled,
  planRole = "assigned",
  createMode = "strict",
}) => {
  const [saving, setSaving] = useState(false);
  const [recipeName, setRecipeName] = useState("");
  const [notes, setNotes] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [servings, setServings] = useState(1);
  const [ingredientsText, setIngredientsText] = useState("");
  const [instructions, setInstructions] = useState("");
  const [servingSuggestion, setServingSuggestion] = useState("");
  const [portion, setPortion] = useState(1);
  const [pickedMealTimeId, setPickedMealTimeId] = useState(mealTimeId || "");

  /** "create" = build a new recipe here (optionally save it); "saved" = pick from library. */
  const [recipeSource, setRecipeSource] = useState("create");
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [loadingSavedRecipes, setLoadingSavedRecipes] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [saveAsRecipe, setSaveAsRecipe] = useState(false);

  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);
  const effectiveMealTimeId = mealTimeId || pickedMealTimeId;

  useEffect(() => {
    if (open) {
      setRecipeName("");
      setNotes("");
      setClientNote("");
      setServings(1);
      setIngredientsText("");
      setInstructions("");
      setServingSuggestion("");
      setPortion(1);
      setPickedMealTimeId(mealTimeId || mealTimes[0]?.id || "");
      setFromDate(isoDate(today));
      setTillDate(isoDate(addDays(today, 7)));
      setAssignmentMode("weekly");
      setWeekdays([...ALL_WEEKDAY_VALUES]);
      setRecipeSource("create");
      setSelectedRecipeId("");
      setSaveAsRecipe(false);

      setLoadingSavedRecipes(true);
      companyAdminRecipesAPI
        .getAll({ limit: 100 })
        .then((res) => setSavedRecipes(res.data.recipes || []))
        .catch(() => setSavedRecipes([]))
        .finally(() => setLoadingSavedRecipes(false));
    }
  }, [open, today, mealTimeId, mealTimes]);

  const parseIngredients = (text) =>
    String(text || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSelectSavedRecipe = (recipeId) => {
    setSelectedRecipeId(recipeId);
    const recipe = savedRecipes.find((r) => r._id === recipeId);
    if (!recipe) return;
    setRecipeName(recipe.name || "");
    setServings(recipe.servings || 1);
    setIngredientsText((recipe.ingredients || []).join("\n"));
    setInstructions(recipe.instructions || "");
    setServingSuggestion(recipe.serving_suggestion || "");
  };

  const handleSubmit = async () => {
    if (!enabled) {
      return toast.error("Food add is not available right now");
    }
    const name = String(recipeName || "").trim();
    const ingredients = parseIngredients(ingredientsText);
    if (!name) return toast.error("Please enter a recipe name");
    if (ingredients.length === 0) {
      return toast.error("Add ingredients (one per line with quantities)");
    }
    if (!effectiveMealTimeId) return toast.error("Select a meal time first");
    const isMealMode = createMode === "meal";
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }

    setSaving(true);
    try {
      const res = await companyAdminCustomersAPI.strictCreateFromRecipe({
        user_id: userId,
        meal_time_id: effectiveMealTimeId,
        recipe_name: name,
        ingredients,
        servings: Number(servings) > 0 ? Number(servings) : 1,
        instructions: String(instructions || "").trim() || undefined,
        serving_suggestion: String(servingSuggestion || "").trim() || undefined,
        client_note: String(clientNote || "").trim() || null,
        ...(isMealMode
          ? {
              as_meal_food: true,
              date: clientNowDateString(),
              from_date: fromDate,
              till_date: tillDate,
              assignment_mode: assignmentMode,
              weekdays:
                assignmentMode === "weekly"
                  ? [...ALL_WEEKDAY_VALUES]
                  : weekdays,
              item_type: "food",
            }
          : {
              portion: normalizePortionInput(portion),
              from_date: fromDate,
              till_date: tillDate,
              assignment_mode: assignmentMode,
              weekdays:
                assignmentMode === "weekly"
                  ? [...ALL_WEEKDAY_VALUES]
                  : weekdays,
              plan_role: "assigned",
            }),
      });
      toast.success("Food added from recipe");
      onCreated?.(res.data);

      if (recipeSource === "create" && saveAsRecipe) {
        try {
          await companyAdminRecipesAPI.create({
            name,
            notes: String(notes || "").trim() || null,
            servings: Number(servings) > 0 ? Number(servings) : 1,
            ingredients,
            instructions: String(instructions || "").trim() || null,
            serving_suggestion:
              String(servingSuggestion || "").trim() || null,
            source: "manual",
          });
          toast.success("Saved to Recipes for reuse");
        } catch {
          toast.error("Food was added, but saving the recipe failed");
        }
      }

      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to create food from recipe",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const canSubmit =
    enabled &&
    String(recipeName || "").trim() &&
    parseIngredients(ingredientsText).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}
        data-testid="add-food-by-recipe-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-primary" />
            Add Food by Recipe
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {!enabled && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground">
              Foods are not available for this customer right now.
            </div>
          )}

          <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            1) Enter a recipe name and ingredients (or pick a saved recipe). 2)
            Review the list, then create food — nutrition is estimated for{" "}
            <span className="font-semibold">one serving</span> of the recipe.
          </div>

          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setRecipeSource("create")}
              disabled={!enabled || saving}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
                recipeSource === "create"
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
              data-testid="recipe-source-create"
            >
              <Sparkles className="w-4 h-4" />
              Create Recipe
            </button>
            <button
              type="button"
              onClick={() => setRecipeSource("saved")}
              disabled={!enabled || saving}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
                recipeSource === "saved"
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
              data-testid="recipe-source-saved"
            >
              <BookOpen className="w-4 h-4" />
              From Saved Recipe
            </button>
          </div>

          {recipeSource === "saved" && (
            <div>
              <Label className="text-foreground">Saved recipe</Label>
              <select
                className="form-select"
                value={selectedRecipeId}
                onChange={(e) => handleSelectSavedRecipe(e.target.value)}
                disabled={!enabled || saving || loadingSavedRecipes}
                data-testid="recipe-picker"
              >
                <option value="">
                  {loadingSavedRecipes
                    ? "Loading recipes…"
                    : savedRecipes.length === 0
                      ? "No saved recipes yet"
                      : "Select a recipe"}
                </option>
                {savedRecipes.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-muted-foreground mt-1">
                Fields below are filled in from the recipe — tweak them if
                needed before creating the food.
              </div>
            </div>
          )}

          {!mealTimeId && (
            <div>
              <Label className="text-foreground">Meal time</Label>
              <select
                className="form-select"
                value={pickedMealTimeId}
                onChange={(e) => setPickedMealTimeId(e.target.value)}
                disabled={!enabled || saving}
              >
                <option value="">Select meal time</option>
                {mealTimes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.meal_type || m.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-foreground">Recipe name</Label>
            <Input
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              placeholder="e.g. Grilled chicken with quinoa salad"
              disabled={!enabled || saving}
            />
          </div>

          {recipeSource === "create" && (
            <div>
              <Label className="text-foreground">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. low oil, Pakistani style, no dairy"
                disabled={!enabled || saving}
                data-testid="recipe-notes-input-food"
              />
            </div>
          )}

          <div>
            <Label className="text-foreground flex items-center gap-1">
              <Info className="w-3.5 h-3.5" />
              Coach note (visible in app)
            </Label>
            <Textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              rows={2}
              placeholder="e.g. Eat this 30 min before workout"
              disabled={!enabled || saving}
              className="mt-1 resize-y"
              data-testid="food-client-note-recipe"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-foreground">Recipe servings</Label>
              <Input
                type="number"
                min="1"
                max="12"
                step="1"
                value={servings}
                onChange={(e) =>
                  setServings(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                disabled={!enabled || saving}
              />
            </div>
            <div>
              <Label className="text-foreground">Plan portion</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={portion}
                onChange={(e) =>
                  setPortion(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                placeholder="1"
                disabled={!enabled || saving}
              />
            </div>
          </div>

          <div>
            <Label className="text-foreground">
              Ingredients (one per line, with amounts)
            </Label>
            <Textarea
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
              placeholder={
                "200 g chicken breast\n1 cup cooked quinoa\n1 tbsp olive oil"
              }
              rows={5}
              disabled={!enabled || saving}
              className="resize-y min-h-[100px] font-mono text-sm"
            />
          </div>

          <div>
            <Label className="text-foreground">
              One serving looks like (optional)
            </Label>
            <Input
              value={servingSuggestion}
              onChange={(e) => setServingSuggestion(e.target.value)}
              placeholder="e.g. ~320 g plated portion"
              disabled={!enabled || saving}
            />
          </div>

          <div>
            <Label className="text-foreground">Instructions (optional)</Label>
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Brief prep / cook method"
              rows={2}
              disabled={!enabled || saving}
              className="resize-y min-h-[60px]"
            />
          </div>

          {recipeSource === "create" && (
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={saveAsRecipe}
                onChange={(e) => setSaveAsRecipe(e.target.checked)}
                className="rounded border-input"
                disabled={saving}
                data-testid="save-as-recipe"
              />
              Save this recipe for reuse with other customers
            </label>
          )}

          <StrictSchedulePicker
            fromDate={fromDate}
            tillDate={tillDate}
            onFromChange={(v) => {
              setFromDate(v);
              if (v) {
                const d = new Date(`${v}T00:00:00`);
                setTillDate(isoDate(addDays(d, 7)));
              }
            }}
            onTillChange={setTillDate}
            assignmentMode={assignmentMode}
            weekdays={weekdays}
            onModeChange={setAssignmentMode}
            onWeekdaysChange={setWeekdays}
          />
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !canSubmit}
            data-testid="add-food-by-recipe-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating food…
              </>
            ) : (
              <>
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                Create food by AI
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Copy an existing food into a new schedule / meal time (keeps original). */
const CopyStrictFoodModal = ({
  open,
  onClose,
  plan,
  mealTimeId,
  mealTimes = [],
  onCopied,
  /** "strict" = StrictPlanDiet copy; "meal" = moderate/flexible Food copy */
  copyMode = "strict",
}) => {
  const isMealCopy = copyMode === "meal";
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");
  const [portion, setPortion] = useState(1);
  const [clientNote, setClientNote] = useState("");
  const [targetMealTimeId, setTargetMealTimeId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [assignmentMode, setAssignmentMode] = useState("weekly");
  const [weekdays, setWeekdays] = useState([...ALL_WEEKDAY_VALUES]);

  const resolveMealTimeId = (raw) => {
    if (raw == null || raw === "") return "";
    const asStr = String(raw);
    const match = (mealTimes || []).find(
      (m) =>
        String(m.id) === asStr ||
        String(m._id) === asStr ||
        String(m.meal_time_id) === asStr,
    );
    return match ? String(match.id) : asStr;
  };

  useEffect(() => {
    if (!open || !plan) return;
    const f = plan.food || {};
    setName(f.name || plan.name || "");
    setDescription(f.description || plan.description || "");
    setCalories(f.calories ?? plan.calories ?? "");
    setFat(f.fat ?? plan.fat ?? "");
    setCarbs(f.carbs ?? plan.carbs ?? "");
    setProtein(f.protein ?? plan.protein ?? plan.proteins ?? "");
    setPortion(normalizePortionInput(plan.portion ?? 1));
    setClientNote(plan.client_note || "");
    setFromDate(toDateInputValue(plan.from_date) || isoDate(new Date()));
    setTillDate(
      toDateInputValue(plan.till_date) || isoDate(addDays(new Date(), 7)),
    );
    const schedule = normalizeWeekdaysState(plan);
    setAssignmentMode(schedule.assignmentMode);
    setWeekdays(schedule.weekdays);
    setTargetMealTimeId(
      resolveMealTimeId(mealTimeId || plan.meal_time_id) ||
        String(mealTimes?.[0]?.id || ""),
    );
  }, [open, plan, mealTimeId, mealTimes]);

  const sourceMealTimeId = resolveMealTimeId(mealTimeId || plan?.meal_time_id);
  const sourceMealName =
    (mealTimes || []).find((m) => String(m.id) === String(sourceMealTimeId))
      ?.name || "Current meal";
  const targetMealName =
    (mealTimes || []).find((m) => String(m.id) === String(targetMealTimeId))
      ?.name || "Selected meal";
  const isCrossMeal =
    targetMealTimeId &&
    sourceMealTimeId &&
    String(targetMealTimeId) !== String(sourceMealTimeId);

  const handleCopy = async () => {
    if (!plan?.id) return;
    if (!targetMealTimeId) return toast.error("Select a target meal time");
    if (!fromDate || !tillDate) return toast.error("Please select dates");
    if (assignmentMode === "custom" && weekdays.length === 0) {
      return toast.error("Select at least one weekday");
    }
    setSaving(true);
    try {
      const payload = {
        meal_time_id: targetMealTimeId,
        from_date: fromDate,
        till_date: tillDate,
        assignment_mode: assignmentMode,
        weekdays:
          assignmentMode === "weekly" ? [...ALL_WEEKDAY_VALUES] : weekdays,
        client_note: String(clientNote || "").trim() || null,
        food: {
          name,
          description,
          calories: calories === "" ? null : Number(calories),
          fat: fat === "" ? null : Number(fat),
          carbs: carbs === "" ? null : Number(carbs),
          protein: protein === "" ? null : Number(protein),
        },
      };
      if (isMealCopy) {
        payload.date = clientNowDateString();
        await companyAdminCustomersAPI.mealFoodCopy(plan.id, payload);
      } else {
        payload.portion = normalizePortionInput(portion);
        await companyAdminCustomersAPI.strictCopy(plan.id, payload);
      }
      toast.success(
        isCrossMeal
          ? `Food copied to ${targetMealName}`
          : "Food copied to selected days",
      );
      onCopied?.(targetMealTimeId);
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to copy food",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-2xl max-h-[75vh] app-modal-shell overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="copy-strict-food-modal"
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Copy className="w-4 h-4" />
              Copy food
            </h3>
            <div className="text-xs text-muted-foreground mt-1">
              Copy to another meal time and/or days. Original stays unchanged.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <Label className="text-foreground">Target meal time</Label>
            <select
              className="form-select mt-1"
              value={targetMealTimeId}
              onChange={(e) => setTargetMealTimeId(e.target.value)}
              data-testid="copy-target-meal-time"
            >
              {(mealTimes || []).map((m) => (
                <option key={String(m.id)} value={String(m.id)}>
                  {m.name}
                  {String(sourceMealTimeId) === String(m.id)
                    ? " (current)"
                    : ""}
                </option>
              ))}
            </select>
            <div className="text-xs text-muted-foreground mt-1">
              From{" "}
              <span className="font-medium text-foreground">
                {sourceMealName}
              </span>
              {isCrossMeal ? (
                <>
                  {" "}
                  →{" "}
                  <span className="font-medium text-foreground">
                    {targetMealName}
                  </span>
                </>
              ) : (
                ". Choose another meal to copy across mealtimes."
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-foreground">Food name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-foreground">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-foreground flex items-center gap-1">
                <Info className="w-3.5 h-3.5" />
                Coach note (visible in app)
              </Label>
              <Textarea
                value={clientNote}
                onChange={(e) => setClientNote(e.target.value)}
                rows={2}
                placeholder="e.g. Eat this 30 min before workout"
                className="mt-1 resize-y"
                data-testid="copy-food-client-note"
              />
            </div>
            <NumberField
              label="Calories"
              value={calories}
              onChange={setCalories}
            />
            <NumberField
              label="Protein"
              value={protein}
              onChange={setProtein}
            />
            <NumberField label="Fat" value={fat} onChange={setFat} />
            <NumberField label="Carbs" value={carbs} onChange={setCarbs} />
            {!isMealCopy && (
              <div>
                <Label className="text-foreground">Portion</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={portion}
                  onChange={(e) =>
                    setPortion(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                />
              </div>
            )}
          </div>

          <StrictSchedulePicker
            fromDate={fromDate}
            tillDate={tillDate}
            onFromChange={setFromDate}
            onTillChange={setTillDate}
            assignmentMode={assignmentMode}
            weekdays={weekdays}
            onModeChange={setAssignmentMode}
            onWeekdaysChange={setWeekdays}
          />
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCopy} disabled={saving || !targetMealTimeId}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Copying…
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                {isCrossMeal ? `Copy to ${targetMealName}` : "Copy food"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== Moderate (type 2) ====================
const ModerateAddModal = ({
  open,
  onClose,
  userId,
  mealTimeId,
  onCreated,
  enabled,
}) => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));

  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const d = new Date();
      setFromDate(isoDate(d));
      setTillDate(isoDate(addDays(d, 7)));
      setCalories("");
      setFat("");
      setCarbs("");
      setProtein("");
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!enabled) return;
    setSubmitting(true);
    try {
      await companyAdminCustomersAPI.moderateCreate({
        user_id: userId,
        meal_time_id: mealTimeId,
        from_date: fromDate,
        till_date: tillDate,
        calories: calories === "" ? null : Number(calories),
        fat: fat === "" ? null : Number(fat),
        carbs: carbs === "" ? null : Number(carbs),
        protein: protein === "" ? null : Number(protein),
        status: 1,
      });
      toast.success("Added");
      onClose();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">
            Add Calories (Moderate Plan)
          </h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Calories</Label>
              <Input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Fat</Label>
              <Input
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Carbs</Label>
              <Input
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Protein</Label>
              <Input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Start Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  if (e.target.value) {
                    const fd = new Date(e.target.value + "T00:00:00");
                    setTillDate(isoDate(addDays(fd, 7)));
                  }
                }}
              />
            </div>
            <div>
              <Label className="text-foreground">End Date</Label>
              <Input
                type="date"
                value={tillDate}
                onChange={(e) => setTillDate(e.target.value)}
              />
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Default duration is 15 days (auto-filled).
          </div>
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !enabled}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ModerateEditModal = ({ open, onClose, plan, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");
  const [status, setStatus] = useState(1);

  useEffect(() => {
    if (!open || !plan) return;

    const toDateInput = (value) => {
      if (!value) return "";
      if (value instanceof Date) return isoDate(value);
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? "" : isoDate(d);
    };

    setFromDate(toDateInput(plan.from_date));
    setTillDate(toDateInput(plan.till_date));
    setCalories(plan.calories ?? "");
    setFat(plan.fat ?? "");
    setCarbs(plan.carbs ?? "");
    setProtein(plan.protein ?? "");
    setStatus(Number(plan.status ?? 1));
  }, [open, plan]);

  const handleSave = async () => {
    const planId = plan?.id || plan?._id;
    if (!planId) {
      toast.error("Missing plan id");
      return;
    }
    setSaving(true);
    try {
      await companyAdminCustomersAPI.moderateUpdate(planId, {
        from_date: fromDate,
        till_date: tillDate,
        calories: calories === "" ? null : Number(calories),
        fat: fat === "" ? null : Number(fat),
        carbs: carbs === "" ? null : Number(carbs),
        protein: protein === "" ? null : Number(protein),
        status,
      });
      toast.success("Updated");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">
            Edit Calories (Moderate Plan)
          </h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Calories</Label>
              <Input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Fat</Label>
              <Input
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Carbs</Label>
              <Input
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Protein</Label>
              <Input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Start Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">End Date</Label>
              <Input
                type="date"
                value={tillDate}
                onChange={(e) => setTillDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-foreground">Status</Label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
            >
              <option value={0}>Inactive</option>
              <option value={1}>Active</option>
              <option value={2}>Expired</option>
              <option value={3}>Soft Deleted</option>
            </select>
          </div>
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== Relax (type 3) ====================
const RelaxAddModal = ({ open, onClose, userId, onCreated, enabled }) => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [tillDate, setTillDate] = useState(isoDate(addDays(today, 7)));

  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const d = new Date();
      setFromDate(isoDate(d));
      setTillDate(isoDate(addDays(d, 7)));
      setCalories("");
      setFat("");
      setCarbs("");
      setProtein("");
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!enabled) return toast.error("Active entry already exists");

    setSubmitting(true);
    try {
      await companyAdminCustomersAPI.relaxCreate({
        user_id: userId,
        from_date: fromDate,
        till_date: tillDate,
        calories: calories === "" ? null : Number(calories),
        fat: fat === "" ? null : Number(fat),
        carbs: carbs === "" ? null : Number(carbs),
        protein: protein === "" ? null : Number(protein),
      });
      toast.success("Added");
      onClose();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">
            Add Calories
          </h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Calories</Label>
              <Input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Fat</Label>
              <Input
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Carbs</Label>
              <Input
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Protein</Label>
              <Input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Start Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">End Date</Label>
              <Input
                type="date"
                value={tillDate}
                onChange={(e) => setTillDate(e.target.value)}
              />
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Default duration is 15 days (auto-filled).
          </div>
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !enabled}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RelaxEditModal = ({ open, onClose, plan, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [carbs, setCarbs] = useState("");
  const [protein, setProtein] = useState("");
  const [status, setStatus] = useState(1);

  useEffect(() => {
    if (!open) return;
    const toDateInput = (v) => {
      if (!v) return "";
      if (v instanceof Date) return isoDate(v);
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? "" : isoDate(d);
    };
    setFromDate(toDateInput(plan?.from_date));
    setTillDate(toDateInput(plan?.till_date));
    setCalories(plan?.calories ?? "");
    setFat(plan?.fat ?? "");
    setCarbs(plan?.carbs ?? "");
    setProtein(plan?.protein ?? "");
    setStatus(Number(plan?.status ?? 1));
  }, [open, plan]);

  const handleSave = async () => {
    if (!plan?.id) return;
    setSaving(true);
    try {
      await companyAdminCustomersAPI.relaxUpdate(plan.id, {
        from_date: fromDate,
        till_date: tillDate,
        calories: calories === "" ? null : Number(calories),
        fat: fat === "" ? null : Number(fat),
        carbs: carbs === "" ? null : Number(carbs),
        protein: protein === "" ? null : Number(protein),
        status,
      });
      toast.success("Updated");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div className="panel w-full max-w-xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground">Edit Entry</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Calories</Label>
              <Input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Fat</Label>
              <Input
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Carbs</Label>
              <Input
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">Protein</Label>
              <Input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Start Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-foreground">End Date</Label>
              <Input
                type="date"
                value={tillDate}
                onChange={(e) => setTillDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-foreground">Status</Label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
            >
              <option value={0}>Inactive</option>
              <option value={1}>Active</option>
              <option value={2}>Expired</option>
              <option value={3}>Soft Deleted</option>
            </select>
          </div>
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const formatVersionSetDate = (iso) => {
  try {
    if (!iso) return "unknown date";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "unknown date";
  }
};

const versionDisplayLabel = (v) =>
  v?.label ||
  `Plan v${v?.version ?? "?"} — set on ${formatVersionSetDate(v?.set_at)}`;

const SnapshotColumn = ({ title, subtitle, snapshot }) => {
  const foods = snapshot?.foods || [];
  const moderate = snapshot?.moderate_macros || [];
  const relax = snapshot?.relax_macros || [];

  const byMeal = useMemo(() => {
    const map = new Map();
    for (const f of foods) {
      const key = f.meal_time_name || f.meal_time_id || "Meal";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return [...map.entries()];
  }, [foods]);

  return (
    <div className="rounded-md border border-border overflow-hidden min-h-[200px]">
      <div className="px-3 py-2 bg-muted border-b border-border">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {subtitle ? (
          <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        ) : null}
      </div>
      <div className="p-3 space-y-3 max-h-[50vh] overflow-y-auto text-sm">
        {relax.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Daily calories
            </div>
            {relax.map((r, i) => (
              <div
                key={r.source_plan_id || i}
                className="text-muted-foreground py-1 border-b border-border/60 last:border-0"
              >
                {r.calories ?? "-"} kcal · F {r.fat ?? "-"} · C {r.carbs ?? "-"}{" "}
                · P {r.protein ?? "-"}
              </div>
            ))}
          </div>
        )}
        {moderate.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Meal macros
            </div>
            {moderate.map((m, i) => (
              <div
                key={m.source_plan_id || i}
                className="text-muted-foreground py-1 border-b border-border/60 last:border-0"
              >
                <span className="font-medium text-foreground">
                  {m.meal_time_name || `Meal ${m.meal_time_id ?? ""}`}
                </span>
                : {m.calories ?? "-"} kcal · F {m.fat ?? "-"} · C{" "}
                {m.carbs ?? "-"} · P {m.protein ?? "-"}
              </div>
            ))}
          </div>
        )}
        {byMeal.length > 0 ? (
          byMeal.map(([mealName, items]) => (
            <div key={mealName}>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                {mealName}
              </div>
              {items.map((f, i) => (
                <div
                  key={f.source_plan_id || `${mealName}-${i}`}
                  className="py-1.5 border-b border-border/60 last:border-0"
                >
                  <div className="font-medium text-foreground">
                    {f.food_name || "Food"}
                    {f.portion != null ? (
                      <span className="text-muted-foreground font-normal">
                        {" "}
                        ×{f.portion}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {f.calories != null ? `${f.calories} kcal` : "—"}
                    {f.fat != null ? ` · F ${f.fat}` : ""}
                    {f.carbs != null ? ` · C ${f.carbs}` : ""}
                    {f.protein != null ? ` · P ${f.protein}` : ""}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : foods.length === 0 &&
          moderate.length === 0 &&
          relax.length === 0 ? (
          <div className="text-muted-foreground">No plan items in this snapshot.</div>
        ) : null}
      </div>
    </div>
  );
};

const PlanVersionHistoryModal = ({
  open,
  onClose,
  userId,
  planType,
  canEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compare, setCompare] = useState(null);

  const loadVersions = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.dietPlanVersionsList({
        user_id: userId,
        diet_plan_type: planType,
      });
      setVersions(res.data?.versions || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load plan versions");
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCompare(null);
      loadVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, planType]);

  const handleSaveVersion = async () => {
    setSaving(true);
    try {
      const res = await companyAdminCustomersAPI.dietPlanVersionsCreate({
        user_id: userId,
        diet_plan_type: planType,
      });
      toast.success(
        res.data?.message || res.data?.version?.label || "Plan version saved",
      );
      await loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save plan version");
    } finally {
      setSaving(false);
    }
  };

  const handleCompare = async (versionId) => {
    setCompareLoading(true);
    try {
      const res =
        await companyAdminCustomersAPI.dietPlanVersionsCompare(versionId);
      setCompare(res.data || null);
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Failed to compare with current plan",
      );
      setCompare(null);
    } finally {
      setCompareLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} role="presentation">
      <div
        className="panel w-full max-w-4xl mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}
        data-testid="plan-version-history-modal"
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Meal plan version history
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground max-w-xl">
              Save a snapshot of the current plan (e.g. “Plan v3 — set on 1 Jun
              2026”), then open any past version to compare it with what’s live
              now.
            </p>
            {canEdit && (
              <Button
                onClick={handleSaveVersion}
                disabled={saving || loading}
                data-testid="plan-version-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save version
                  </>
                )}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6">
              No versions yet. Click <strong>Save version</strong> to snapshot
              the current meal plan.
            </div>
          ) : (
            <div className="rounded-md border border-border divide-y divide-border">
              {versions.map((v) => (
                <div
                  key={v.id || v._id}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {versionDisplayLabel(v)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {(v.summary?.foods ?? 0) +
                        (v.summary?.moderate_macros ?? 0) +
                        (v.summary?.relax_macros ?? 0)}{" "}
                      item(s)
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCompare(v.id || v._id)}
                    disabled={compareLoading}
                    data-testid={`plan-version-compare-${v.version}`}
                  >
                    Compare with current
                  </Button>
                </div>
              ))}
            </div>
          )}

          {compareLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          )}

          {compare && !compareLoading && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-foreground">
                Compare
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SnapshotColumn
                  title={versionDisplayLabel(compare.version)}
                  subtitle="Saved snapshot"
                  snapshot={compare.version?.snapshot}
                />
                <SnapshotColumn
                  title={compare.current?.label || "Current plan"}
                  subtitle="Live plan now"
                  snapshot={compare.current?.snapshot}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== Main ====================
const DietPlanTab = ({ customer, onSummaryRefresh, canEdit = true }) => {
  const { companyConfig } = useCompanyAdminAuth();
  const canGenerateAiPlan = companyConfig?.allow_generate_ai_plan === true;
  const userId = customer.user_id;
  const planType = Number(customer?.survey?.diet_plan_type || 3);
  // Strict = StrictPlanDiet.assign. Moderate/Flexible = customer meal Food (like mobile).
  const isMealFoodMode = planType === 2 || planType === 3;
  const foodPlanRole = "assigned";
  const supportsFoodAdd = planType === 1 || planType === 2 || planType === 3;

  const [mealTimesLoading, setMealTimesLoading] = useState(true);
  const [mealTimes, setMealTimes] = useState([]);
  const [activeMealTimeId, setActiveMealTimeId] = useState(null);

  const [plansLoading, setPlansLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  /** Moderate/Flexible: foods for selected meal (Food.meal_time_id), not StrictPlanDiet */
  const [mealFoods, setMealFoods] = useState([]);
  /** Strict list: null = all days; 0–6 = filter by weekday (JS getDay) */
  const [weekdayFilter, setWeekdayFilter] = useState(null);

  const [moderatePlans, setModeratePlans] = useState([]);

  const [relaxPlans, setRelaxPlans] = useState([]);

  const [mealStatus, setMealStatus] = useState({});
  /** Flexible only: meal time ids the customer has enabled on their side. */
  const [enabledMealTimeIds, setEnabledMealTimeIds] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addSupplementOpen, setAddSupplementOpen] = useState(false);
  const [addAiOpen, setAddAiOpen] = useState(false);
  const [generatePlanOpen, setGeneratePlanOpen] = useState(false);
  const [addRecipeOpen, setAddRecipeOpen] = useState(false);
  const [microOpen, setMicroOpen] = useState(false);
  const [microData, setMicroData] = useState(null);

  const [subFoodsOpen, setSubFoodsOpen] = useState(false);
  const [subFoodsModal, setSubFoodsModal] = useState({
    foodName: "",
    items: [],
  });

  const [editOpen, setEditOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyPlan, setCopyPlan] = useState(null);
  const [editPlan, setEditPlan] = useState(null);

  const [moderateAddOpen, setModerateAddOpen] = useState(false);
  const [moderateEditOpen, setModerateEditOpen] = useState(false);
  const [moderateEditPlan, setModerateEditPlan] = useState(null);

  const [relaxAddOpen, setRelaxAddOpen] = useState(false);
  const [relaxEditOpen, setRelaxEditOpen] = useState(false);
  const [relaxEditPlan, setRelaxEditPlan] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    type: null,
    planId: null,
  });
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const moderateHasActive =
    planType === 2 && (moderatePlans || []).some((p) => Number(p.status) === 1);
  const relaxHasActive =
    planType === 3 && (relaxPlans || []).some((p) => Number(p.status) === 1);

  const displayedStrictPlans = useMemo(() => {
    if (weekdayFilter == null) return plans;
    return (plans || []).filter((p) => {
      const { weekdays } = normalizeWeekdaysState(p);
      return weekdays.includes(Number(weekdayFilter));
    });
  }, [plans, weekdayFilter]);

  const weekdayFilterLabel =
    weekdayFilter == null
      ? null
      : WEEKDAY_OPTIONS.find((d) => d.value === Number(weekdayFilter))?.label;

  const displayedMealFoods = useMemo(() => {
    if (weekdayFilter == null) return mealFoods;
    return (mealFoods || []).filter((p) => {
      const { weekdays } = normalizeWeekdaysState(p);
      return weekdays.includes(Number(weekdayFilter));
    });
  }, [mealFoods, weekdayFilter]);

  const fetchMealTimes = async () => {
    setMealTimesLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getMealTimes();
      const mts = res.data.meal_times || [];
      setMealTimes(mts);
      setActiveMealTimeId(mts[0]?.id || null);
    } catch {
      toast.error("Failed to load meal times");
    } finally {
      setMealTimesLoading(false);
    }
  };

  const fetchMealStatus = async () => {
    if (!userId) {
      setMealStatus({});
      setEnabledMealTimeIds([]);
      return;
    }

    try {
      if (planType === 1) {
        const res = await companyAdminCustomersAPI.strictMealStatus({
          user_id: userId,
        });
        setMealStatus(res.data.meal_status || {});
        return;
      }
      if (planType === 2) {
        const res = await companyAdminCustomersAPI.moderateMealStatus({
          user_id: userId,
        });
        setMealStatus(res.data.meal_status || {});
        return;
      }
      if (planType === 3) {
        const res = await companyAdminCustomersAPI.relaxMealStatus({
          user_id: userId,
        });
        setMealStatus(res.data.meal_status || {});
        setEnabledMealTimeIds(res.data.enabled_meal_time_ids || []);
        return;
      }
      setMealStatus({});
    } catch {
      setMealStatus({});
      setEnabledMealTimeIds([]);
    }
  };

  const sameMealTimeId = (a, b) => {
    if (a == null || b == null || a === "" || b === "") return false;
    if (String(a) === String(b)) return true;
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
  };

  /** Merge meal-status counts across numeric id / ObjectId key variants. */
  const getMealStatusCounts = (mealTimeId) => {
    const mt = (mealTimes || []).find(
      (m) =>
        sameMealTimeId(m.id, mealTimeId) || sameMealTimeId(m._id, mealTimeId),
    );
    const keys = new Set(
      [mealTimeId, mt?.id, mt?._id]
        .filter((k) => k != null && k !== "")
        .map((k) => String(k)),
    );
    if (mealTimeId != null && Number.isFinite(Number(mealTimeId))) {
      keys.add(String(Number(mealTimeId)));
    }

    const merged = {};
    for (const key of keys) {
      const s = mealStatus?.[key];
      if (!s) continue;
      for (const [st, count] of Object.entries(s)) {
        merged[st] = (merged[st] || 0) + Number(count || 0);
      }
    }

    // Fold in locally loaded rows for the selected meal time so an expired food
    // in the open list always paints the box red (even if meal-status is stale).
    const bumpLocal = (status) => {
      const st = String(status ?? 0);
      merged[st] = (merged[st] || 0) + 1;
    };

    if (sameMealTimeId(activeMealTimeId, mealTimeId)) {
      if (planType === 1) {
        for (const p of plans || []) bumpLocal(p.status);
      }
      if (planType === 2 || planType === 3) {
        for (const p of mealFoods || []) {
          bumpLocal(p.status ?? p.food?.status);
        }
      }
      if (planType === 2) {
        for (const p of moderatePlans || []) bumpLocal(p.status);
      }
    }

    return merged;
  };

  // Returns "green", "red", or "none".
  // Priority: any expired/inactive → red (even if other foods are active),
  // then active → green, then Flexible calorie / enabled-meal flags.
  const mealTabColor = (mealTimeId) => {
    const s = getMealStatusCounts(mealTimeId);
    const countActive = Number(s["1"] || 0);
    const countExpiredOrInactive =
      Number(s["2"] || 0) + Number(s["0"] || 0) + Number(s["3"] || 0);
    // Client rule: expired/inactive meal content must show red.
    if (countExpiredOrInactive > 0) return "red";
    if (countActive > 0) return "green";
    if (planType === 3) {
      // Flexible calories are customer-level (not per meal). If they are
      // expired/inactive with no active calorie row, mark meal boxes red.
      const hasActiveCalories = (relaxPlans || []).some(
        (p) => Number(p.status) === 1,
      );
      const hasExpiredOrInactiveCalories = (relaxPlans || []).some((p) => {
        const st = Number(p.status);
        return st === 0 || st === 2 || st === 3;
      });
      if (!hasActiveCalories && hasExpiredOrInactiveCalories) return "red";
      if (
        enabledMealTimeIds.some((id) => Number(id) === Number(mealTimeId))
      ) {
        return "green";
      }
    }
    return "none";
  };

  /**
   * Silently refetch diet lists + meal-time colors after create/update/delete.
   * Does not reload the page or flash the full-page customer loader.
   */
  const refreshAfterDietMutation = async (targetMealTimeId) => {
    const mealId = targetMealTimeId || activeMealTimeId;
    await fetchMealStatus();
    if (planType === 1) {
      if (mealId) await fetchPlans(mealId, { manageLoading: false });
    } else if (planType === 2) {
      if (mealId) {
        await Promise.all([
          fetchModeratePlans(mealId, { manageLoading: false }),
          fetchMealFoods(mealId),
        ]);
      }
    } else if (planType === 3) {
      await fetchRelaxPlans({ manageLoading: false });
      if (mealId) await fetchMealFoods(mealId);
    }
    onSummaryRefresh?.();
  };

  /** Strict plan foods only. */
  const fetchPlans = async (mealTimeId, { manageLoading = true } = {}) => {
    if (!mealTimeId) return;
    if (manageLoading) setPlansLoading(true);
    try {
      const res = await companyAdminCustomersAPI.strictList({
        user_id: userId,
        plan_role: "assigned",
        meal_time_id: mealTimeId,
      });
      const strictPlans = (res.data.plans || []).map((p) => ({
        ...p,
        id: p.id ?? p._id,
      }));
      setPlans(strictPlans);
    } catch {
      if (Array.isArray(customer.strict_diet_plans)) {
        const all = customer.strict_diet_plans || [];
        const filtered = all
          .filter((p) => String(p.meal_time_id) === String(mealTimeId))
          .filter((p) => p.plan_role !== "recommendation")
          .map((p) => ({
            ...p,
            id: p.id ?? p._id,
          }));
        setPlans(filtered);
      } else {
        toast.error("Failed to load strict diet plan");
        setPlans([]);
      }
    } finally {
      if (manageLoading) setPlansLoading(false);
    }
  };

  /** Moderate / Flexible: customer Foods for meal time (same as mobile My Food). */
  const fetchMealFoods = async (mealTimeId) => {
    if (!mealTimeId) {
      setMealFoods([]);
      return;
    }
    try {
      const res = await companyAdminCustomersAPI.mealFoodList({
        user_id: userId,
        meal_time_id: mealTimeId,
        type: planType,
      });
      setMealFoods(
        (res.data.foods || []).map((f) => ({
          ...f,
          id: f.id ?? f._id,
        })),
      );
    } catch {
      toast.error("Failed to load meal foods");
      setMealFoods([]);
    }
  };

  const fetchModeratePlans = async (
    mealTimeId,
    { manageLoading = true } = {},
  ) => {
    if (!mealTimeId) return;
    if (manageLoading) setPlansLoading(true);
    try {
      // Always fetch latest moderate plans from API so UI reflects changes immediately
      const res = await companyAdminCustomersAPI.moderateList({
        user_id: userId,
        meal_time_id: mealTimeId,
      });
      const plans = (res.data.plans || []).map((p) => ({
        ...p,
        id: p.id ?? p._id,
      }));
      setModeratePlans(plans);
    } catch {
      // Fallback to any preloaded data on error
      if (Array.isArray(customer.moderate_diet_plans)) {
        const all = customer.moderate_diet_plans || [];
        const filtered = all
          .filter((p) => String(p.meal_time_id) === String(mealTimeId))
          .map((p) => ({
            ...p,
            id: p.id ?? p._id,
          }));
        setModeratePlans(filtered);
      } else {
        toast.error("Failed to load moderate diet plan");
        setModeratePlans([]);
      }
    } finally {
      if (manageLoading) setPlansLoading(false);
    }
  };

  const fetchRelaxPlans = async ({ manageLoading = true } = {}) => {
    if (manageLoading) setPlansLoading(true);
    try {
      const res = await companyAdminCustomersAPI.relaxList({ user_id: userId });
      setRelaxPlans(res.data.plans || []);
    } catch {
      toast.error("Failed to load moderate relax");
      setRelaxPlans([]);
    } finally {
      if (manageLoading) setPlansLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!userId) return;
    setPdfDownloading(true);
    try {
      const res = await companyAdminCustomersAPI.mealPlanPdf({
        user_id: userId,
        diet_plan_type: planType,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "weekly-meal-plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Failed to download meal plan PDF",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  useEffect(() => {
    fetchMealTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType]);

  useEffect(() => {
    if (planType === 3) {
      fetchRelaxPlans();
      if (activeMealTimeId) fetchMealFoods(activeMealTimeId);
      else setMealFoods([]);
      return;
    }
    if (!activeMealTimeId) return;
    if (planType === 1) {
      fetchPlans(activeMealTimeId);
    }
    if (planType === 2) {
      fetchModeratePlans(activeMealTimeId);
      fetchMealFoods(activeMealTimeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMealTimeId, planType]);

  useEffect(() => {
    fetchMealStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType, userId]);

  const handleStrictDelete = async (planId) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        "Are you sure you want to delete this food from the diet plan?",
      )
    )
      return;
    try {
      if (isMealFoodMode) {
        await companyAdminCustomersAPI.mealFoodDelete(planId);
        toast.success("Deleted");
      } else {
        await companyAdminCustomersAPI.strictDelete(planId);
        toast.success("Deleted");
      }
      await refreshAfterDietMutation();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleModerateDelete = async (planId) => {
    if (
      !window.confirm(
        "Soft delete this entry? It will be marked as deleted but kept in the database.",
      )
    )
      return;
    try {
      await companyAdminCustomersAPI.moderateDelete(planId, false);
      toast.success("Entry soft deleted");
      await refreshAfterDietMutation();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleRelaxDelete = async (planId) => {
    setDeleteConfirm({ type: "relax", planId });
    setDeleteConfirmOpen(true);
  };

  const performDelete = async (mode) => {
    const { type, planId } = deleteConfirm || {};
    if (!type || !planId) return;

    try {
      if (type === "moderate") {
        await companyAdminCustomersAPI.moderateDelete(planId, mode === "hard");
        toast.success(mode === "hard" ? "Hard deleted" : "Soft deleted");
        await refreshAfterDietMutation();
        return;
      }
      if (type === "relax") {
        await companyAdminCustomersAPI.relaxDelete(planId, mode === "hard");
        toast.success(mode === "hard" ? "Hard deleted" : "Soft deleted");
        await refreshAfterDietMutation();
        return;
      }
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteConfirm({ type: null, planId: null });
    }
  };

  const DeleteConfirmModal = () => {
    if (!deleteConfirmOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirm({ type: null, planId: null }); }} role="presentation">
        <div className="panel w-full max-w-md mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <h3 className="text-lg font-semibold text-foreground">
              Delete Confirmation
            </h3>
            <button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteConfirm({ type: null, planId: null });
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="app-modal-body p-6 flex-1 min-h-0 overflow-y-auto">
            <div className="text-sm text-muted-foreground">
              Choose how you want to delete this entry.
            </div>
          </div>

          <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteConfirm({ type: null, planId: null });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => performDelete("soft")}
            >
              Soft Delete
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => performDelete("hard")}
            >
              Hard Delete
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <div className="text-sm font-semibold text-foreground">Diet Plan</div>
          <div className="text-xs text-muted-foreground mt-1">
            {planType === 1
              ? "Strict Diet Plan supports adding food from Food Bank."
              : planType === 2
                ? "Moderate Diet Plan: macros per meal, plus foods/supplements they can log in the app."
                : "Flexible Diet Plan: macros plus foods/supplements they can log in the app."}
          </div>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <div>
            <div className="text-xs text-muted-foreground">Diet Plan Type</div>
            <div className="text-sm font-semibold text-foreground mt-1">
              {planLabelFromType(planType)}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleDownloadPdf}
              disabled={pdfDownloading}
              data-testid="diet-download-pdf"
            >
              {pdfDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setVersionHistoryOpen(true)}
              data-testid="diet-plan-version-history"
            >
              <History className="w-4 h-4" />
              Version history
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 panel border border-border">
          <div>
            <div className="text-sm font-semibold text-foreground">
              Meal Times
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Select a meal time to view entries.
            </div>
          </div>

          {mealTimesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {mealTimes.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setActiveMealTimeId(m.id)}
                  className={(() => {
                    const color = mealTabColor(m.id ?? m._id);
                    const isActive = activeMealTimeId === m.id;
                    const base =
                      "w-full text-left rounded-md border px-4 py-3";
                    if (color === "green")
                      return `${base} border-green-500 ${isActive ? "bg-green-500/20" : "bg-green-500/10 hover:bg-green-500/20"}`;
                    if (color === "red")
                      return `${base} border-red-400 ${isActive ? "bg-red-400/20" : "bg-red-400/10 hover:bg-red-400/20"}`;
                    return `${base} ${isActive ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted"}`;
                  })()}
                >
                  <div className="text-sm font-semibold text-foreground">
                    {m.meal_type}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {m.description || ""}
                  </div>
                </button>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="mt-4 space-y-2">
                {planType === 1 && (
                  <>
                    <Button
                      className="w-full gap-2"
                      onClick={() => setAddOpen(true)}
                      disabled={!activeMealTimeId}
                      data-testid="diet-add-food"
                    >
                      <Plus className="w-4 h-4" />
                      Add Food
                    </Button>
                  {canGenerateAiPlan && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setGeneratePlanOpen(true)}
                      disabled={!activeMealTimeId}
                      data-testid="diet-generate-ai-plan"
                    >
                      <Sparkles className="w-4 h-4" />
                      Suggest Foods with AI
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => setAddAiOpen(true)}
                    disabled={!activeMealTimeId}
                    data-testid="diet-add-food-by-ai"
                  >
                    <Sparkles className="w-4 h-4" />
                    Add Food by AI
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => setAddRecipeOpen(true)}
                    disabled={!activeMealTimeId}
                    data-testid="diet-add-food-by-recipe"
                  >
                    <UtensilsCrossed className="w-4 h-4" />
                    Add Food by Recipe
                  </Button>
                </>
              )}
              {planType === 2 && (
                <>
                  <Button
                    className="w-full gap-2"
                    onClick={() => setModerateAddOpen(true)}
                    disabled={!activeMealTimeId || moderateHasActive}
                    data-testid="diet-add-calories"
                  >
                    <Plus className="w-4 h-4" />
                    Add Calories
                  </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => {
                        setAddOpen(true);
                      }}
                      disabled={!activeMealTimeId || !moderateHasActive}
                      data-testid="diet-add-food"
                    >
                      <Plus className="w-4 h-4" />
                      Add Food
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => {
                        setAddSupplementOpen(true);
                      }}
                      disabled={!activeMealTimeId || !moderateHasActive}
                      data-testid="diet-add-supplement"
                    >
                      <Plus className="w-4 h-4" />
                      Add Supplement
                    </Button>
                    {canGenerateAiPlan && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => setGeneratePlanOpen(true)}
                        disabled={!activeMealTimeId || !moderateHasActive}
                        data-testid="diet-generate-ai-plan"
                      >
                        <Sparkles className="w-4 h-4" />
                        Suggest Foods with AI
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setAddAiOpen(true)}
                      disabled={!activeMealTimeId || !moderateHasActive}
                      data-testid="diet-add-food-by-ai"
                    >
                      <Sparkles className="w-4 h-4" />
                      Add Food by AI
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setAddRecipeOpen(true)}
                      disabled={!activeMealTimeId || !moderateHasActive}
                      data-testid="diet-add-food-by-recipe"
                    >
                      <UtensilsCrossed className="w-4 h-4" />
                      Add Food by Recipe
                    </Button>
                    {moderateHasActive ? (
                      <div className="text-xs text-muted-foreground mt-2">
                        You already have an Active calorie entry for this meal
                        time. Set it to Inactive/Expired first to add another.
                      </div>
                    ) : (
                      activeMealTimeId && (
                        <div className="text-xs text-muted-foreground mt-2">
                          Define calories for this meal time first — food,
                          supplement, and AI actions unlock once an Active
                          calorie entry exists.
                        </div>
                      )
                    )}
                  </>
                )}
                {planType === 3 && (
                  <>
                    <Button
                      className="w-full gap-2"
                      onClick={() => setRelaxAddOpen(true)}
                      disabled={relaxHasActive}
                      data-testid="diet-add-calories"
                    >
                      <Plus className="w-4 h-4" />
                      Add Calories
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => {
                        setAddOpen(true);
                      }}
                      disabled={!activeMealTimeId}
                      data-testid="diet-add-food"
                    >
                      <Plus className="w-4 h-4" />
                      Add Food
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => {
                        setAddSupplementOpen(true);
                      }}
                      disabled={!activeMealTimeId}
                      data-testid="diet-add-supplement"
                    >
                      <Plus className="w-4 h-4" />
                      Add Supplement
                    </Button>
                    {canGenerateAiPlan && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => setGeneratePlanOpen(true)}
                        disabled={!activeMealTimeId}
                        data-testid="diet-generate-ai-plan"
                      >
                        <Sparkles className="w-4 h-4" />
                        Suggest Foods with AI
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setAddAiOpen(true)}
                      disabled={!activeMealTimeId}
                      data-testid="diet-add-food-by-ai"
                    >
                      <Sparkles className="w-4 h-4" />
                      Add Food by AI
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setAddRecipeOpen(true)}
                      disabled={!activeMealTimeId}
                      data-testid="diet-add-food-by-recipe"
                    >
                      <UtensilsCrossed className="w-4 h-4" />
                      Add Food by Recipe
                    </Button>
                    {relaxHasActive && (
                      <div className="text-xs text-muted-foreground mt-2">
                        You already have an Active calorie entry. Set it to
                        Inactive/Expired first to add another.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeMealTimeId != null && (
              <div className="mt-4 rounded-md border border-border bg-muted px-3 py-2.5 text-xs text-muted-foreground flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary" />
                <span>
                  Coach tips go on each food — use <strong>Coach note</strong>{" "}
                  when adding or editing a food (separate from description). Use{" "}
                  <strong>Add Supplement</strong> for vitamins/supplements.
                </span>
              </div>
            )}
          </div>

          <div className="lg:col-span-8 panel border border-border p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-foreground">
                {planType === 2 || planType === 3 ? "Calories" : "Foods"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {planType === 1 && weekdayFilter != null
                  ? `${displayedStrictPlans.length} of ${plans.length} food${plans.length === 1 ? "" : "s"} on ${weekdayFilterLabel}`
                  : `Meal Time ID: ${activeMealTimeId ?? "-"}`}
              </div>
            </div>
          </div>

          {planType === 1 && (
            <div
              className="px-6 py-3 border-b border-border flex flex-wrap items-center gap-2"
              data-testid="strict-weekday-filter"
            >
              <span className="text-xs font-medium text-muted-foreground mr-1">
                Show foods for
              </span>
              <button
                type="button"
                onClick={() => setWeekdayFilter(null)}
                className={
                  "h-8 px-3 rounded-full border text-xs font-semibold transition-colors " +
                  (weekdayFilter == null
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40")
                }
              >
                All
              </button>
              {WEEKDAY_OPTIONS.map((d) => {
                const active = weekdayFilter === d.value;
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setWeekdayFilter(active ? null : d.value)}
                    className={
                      "h-8 min-w-[2.5rem] px-2 rounded-full border text-xs font-semibold transition-colors " +
                      (active
                        ? "border-primary bg-primary text-white"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40")
                    }
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          )}

          {plansLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : planType === 1 && displayedStrictPlans.length === 0 ? (
            <div className="px-6 py-12 text-sm text-muted-foreground">
              {weekdayFilter != null && plans.length > 0
                ? `No foods assigned for ${weekdayFilterLabel}.`
                : "No entries."}
            </div>
          ) : planType === 3 ? (
            <>
              {relaxPlans.length === 0 ? (
                <div className="px-6 py-8 text-sm text-muted-foreground">
                  No calorie plan entries yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Added
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Calories
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Fat
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Carbs
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Protein
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Status
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Duration
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relaxPlans.map((p) => (
                      <TableRow key={p.id} className="hover:bg-muted">
                        <TableCell className="text-muted-foreground">
                          {(p.created_at || p.createdAt || "").slice(0, 10) ||
                            "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.calories ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.fat ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.carbs ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.protein ?? "-"}
                        </TableCell>
                        <TableCell className={dietStatusClassName(p.status)}>
                          {dietStatusLabel(p.status)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDuration(p.from_date, p.till_date)}
                        </TableCell>
                        <TableCell className="text-right">
                          {canEdit ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-muted"
                                onClick={() => {
                                  setRelaxEditPlan(p);
                                  setRelaxEditOpen(true);
                                }}
                                aria-label="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRelaxDelete(p.id)}
                                aria-label="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="px-6 py-3 border-t border-border text-sm font-semibold text-foreground">
                Foods & Supplements
              </div>
              {displayedMealFoods.length === 0 ? (
                <div className="px-6 py-8 text-sm text-muted-foreground">
                  No foods or supplements for this meal yet. Use{" "}
                  <strong>Add Food</strong> or <strong>Add Supplement</strong>.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Type
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Food
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Serving
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Calories
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Status
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Duration
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Days
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedMealFoods.map((p) => {
                      const food = p.food || p || {};
                      const isSupplement =
                        String(p.item_type || "food").toLowerCase() ===
                        "supplement";
                      const foodStatus = p.status ?? food.status;
                      return (
                        <TableRow key={p.id} className="hover:bg-muted">
                          <TableCell className="text-muted-foreground">
                            {isSupplement ? "Supplement" : "Food"}
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            {food.name || p.name || "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {isSupplement
                              ? `${Number(p.dose_per_day) === 2 ? 2 : 1} / day`
                              : food.serving_size || p.serving_size || "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {isSupplement
                              ? "—"
                              : (food.calories ?? p.calories ?? "-")}
                          </TableCell>
                          <TableCell className={dietStatusClassName(foodStatus)}>
                            {dietStatusLabel(foodStatus)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDuration(p.from_date, p.till_date)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <span
                              className="text-sm"
                              title={formatWeekdaysLabel(p)}
                            >
                              {formatWeekdaysLabel(p)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit ? (
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setEditPlan(p);
                                    setEditOpen(true);
                                  }}
                                  aria-label="Edit food details"
                                  title="Edit food"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setCopyPlan(p);
                                    setCopyOpen(true);
                                  }}
                                  aria-label="Copy food to other days"
                                  title="Copy food"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleStrictDelete(p.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          ) : planType === 2 ? (
            <>
              {moderatePlans.length === 0 ? (
                <div className="px-6 py-8 text-sm text-muted-foreground">
                  No calorie plan entries for this meal yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Added
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Calories
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Fat
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Carbs
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Protein
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Status
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Duration
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moderatePlans.map((p) => (
                      <TableRow key={p.id} className="hover:bg-muted">
                        <TableCell className="text-muted-foreground">
                          {(p.created_at || p.createdAt || "").slice(0, 10) ||
                            "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.calories ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.fat ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.carbs ?? "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.protein ?? "-"}
                        </TableCell>
                        <TableCell
                          className={
                            Number(p.status) === 1
                              ? "text-green-600 font-semibold"
                              : "text-red-600 font-semibold"
                          }
                        >
                          {Number(p.status) === 1
                            ? "Active"
                            : Number(p.status) === 0
                              ? "Inactive"
                              : Number(p.status) === 2
                                ? "Expired"
                                : Number(p.status) === 3
                                  ? "Soft Deleted"
                                  : "Unknown"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDuration(p.from_date, p.till_date)}
                        </TableCell>
                        <TableCell className="text-right">
                          {canEdit ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-muted"
                                onClick={() => {
                                  setModerateEditPlan(p);
                                  setModerateEditOpen(true);
                                }}
                                aria-label="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleModerateDelete(p.id)}
                                aria-label="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="px-6 py-3 border-t border-border text-sm font-semibold text-foreground">
                Foods & Supplements
              </div>
              {displayedMealFoods.length === 0 ? (
                <div className="px-6 py-8 text-sm text-muted-foreground">
                  No foods or supplements for this meal yet. Use{" "}
                  <strong>Add Food</strong> or <strong>Add Supplement</strong>.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Type
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Food
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Serving
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Calories
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Status
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Duration
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Days
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedMealFoods.map((p) => {
                      const food = p.food || p || {};
                      const isSupplement =
                        String(p.item_type || "food").toLowerCase() ===
                        "supplement";
                      const foodStatus = p.status ?? food.status;
                      return (
                        <TableRow key={p.id} className="hover:bg-muted">
                          <TableCell className="text-muted-foreground">
                            {isSupplement ? "Supplement" : "Food"}
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            {food.name || p.name || "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {isSupplement
                              ? `${Number(p.dose_per_day) === 2 ? 2 : 1} / day`
                              : food.serving_size || p.serving_size || "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {isSupplement
                              ? "—"
                              : (food.calories ?? p.calories ?? "-")}
                          </TableCell>
                          <TableCell className={dietStatusClassName(foodStatus)}>
                            {dietStatusLabel(foodStatus)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDuration(p.from_date, p.till_date)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <span
                              className="text-sm"
                              title={formatWeekdaysLabel(p)}
                            >
                              {formatWeekdaysLabel(p)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit ? (
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setEditPlan(p);
                                    setEditOpen(true);
                                  }}
                                  aria-label="Edit food details"
                                  title="Edit food"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setCopyPlan(p);
                                    setCopyOpen(true);
                                  }}
                                  aria-label="Copy food to other days"
                                  title="Copy food"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleStrictDelete(p.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className="overflow-x-auto">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Added
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap min-w-[11rem] max-w-[min(22rem,48vw)] w-[22%]">
                        Food name
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Portion
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Calories
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Fat
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Carbs
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Protein
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Micronutrients
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Subfoods
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Status
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Duration
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap">
                        Days
                      </TableHead>
                      <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right whitespace-nowrap">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedStrictPlans.map((p) => {
                      const food = p.food || {};
                      const subList = normalizeStrictSubFoodList(food);
                      const calDisp = scaleFoodMacroForStrictRow(
                        food.calories,
                        p,
                      );
                      const fatDisp = scaleFoodMacroForStrictRow(food.fat, p);
                      const carbsDisp = scaleFoodMacroForStrictRow(
                        food.carbs,
                        p,
                      );
                      const protDisp = scaleFoodMacroForStrictRow(
                        food.protein,
                        p,
                      );
                      return (
                        <TableRow key={p.id} className="hover:bg-muted">
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {(p.created_at || p.createdAt || "").slice(0, 10) ||
                              "-"}
                          </TableCell>
                          <TableCell className="font-medium text-foreground min-w-[11rem] max-w-[min(22rem,48vw)] w-[22%] p-2 align-middle">
                            <StrictPlanFoodNameCell name={food.name} />
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {p.portion ?? 1}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {calDisp != null ? calDisp : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {fatDisp != null ? fatDisp : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {carbsDisp != null ? carbsDisp : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {protDisp != null ? protDisp : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-muted"
                              onClick={() => {
                                setMicroData(food.micronutrients || {});
                                setMicroOpen(true);
                              }}
                              aria-label="View micronutrients"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {subList.length > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-muted"
                                onClick={() => {
                                  setSubFoodsModal({
                                    foodName: food.name || "",
                                    items: subList,
                                  });
                                  setSubFoodsOpen(true);
                                }}
                                aria-label="View subfoods"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm pl-2">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell
                            className={
                              Number(p.status) === 1
                                ? "text-green-600 font-semibold whitespace-nowrap"
                                : "text-red-600 font-semibold whitespace-nowrap"
                            }
                          >
                            {Number(p.status) === 1
                              ? "Active"
                              : Number(p.status) === 0
                                ? "Inactive"
                                : Number(p.status) === 2
                                  ? "Expired"
                                  : Number(p.status) === 3
                                    ? "Soft Deleted"
                                    : "Unknown"}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {formatDuration(p.from_date, p.till_date)}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap max-w-[10rem]">
                            <span
                              className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium"
                              title={formatWeekdaysLabel(p)}
                            >
                              {formatWeekdaysLabel(p)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {canEdit ? (
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setCopyPlan(p);
                                    setCopyOpen(true);
                                  }}
                                  aria-label="Copy food to other days"
                                  title="Copy food"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    setEditPlan(p);
                                    setEditOpen(true);
                                  }}
                                  aria-label="Edit food details"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleStrictDelete(p.id)}
                                  aria-label="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}
        </div>
      </div>

      <MicronutrientsModal
        open={microOpen}
        onClose={() => {
          setMicroOpen(false);
          setMicroData(null);
        }}
        micronutrients={microData}
      />

      <SubfoodsModal
        open={subFoodsOpen}
        onClose={() => {
          setSubFoodsOpen(false);
          setSubFoodsModal({ foodName: "", items: [] });
        }}
        foodName={subFoodsModal.foodName}
        items={subFoodsModal.items}
      />

      <EditFoodModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditPlan(null);
        }}
        plan={editPlan}
        editMode={isMealFoodMode ? "meal" : "strict"}
        onSaved={() => {
          refreshAfterDietMutation();
        }}
      />

      <CopyStrictFoodModal
        open={copyOpen}
        onClose={() => {
          setCopyOpen(false);
          setCopyPlan(null);
        }}
        plan={copyPlan}
        mealTimeId={activeMealTimeId}
        mealTimes={mealTimes}
        copyMode={isMealFoodMode ? "meal" : "strict"}
        onCopied={(targetId) => {
          if (targetId && String(targetId) !== String(activeMealTimeId)) {
            setActiveMealTimeId(targetId);
          }
          refreshAfterDietMutation(targetId || activeMealTimeId);
        }}
      />

      <AddFoodModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        userId={userId}
        mealTimeId={activeMealTimeId}
        mealTimes={mealTimes}
        createMode={isMealFoodMode ? "meal" : "strict"}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
        enabled={supportsFoodAdd}
        planRole={foodPlanRole}
      />

      <AddSupplementModal
        open={addSupplementOpen}
        onClose={() => setAddSupplementOpen(false)}
        userId={userId}
        mealTimeId={activeMealTimeId}
        mealTimes={mealTimes}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
        enabled={isMealFoodMode && supportsFoodAdd}
      />

      <AddFoodByAiModal
        open={addAiOpen}
        onClose={() => setAddAiOpen(false)}
        userId={userId}
        mealTimeId={activeMealTimeId}
        mealTimes={mealTimes}
        createMode={isMealFoodMode ? "meal" : "strict"}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
        enabled={supportsFoodAdd}
        planRole={foodPlanRole}
      />

      {canGenerateAiPlan && (
        <GenerateAiPlanModal
          open={generatePlanOpen}
          onClose={() => setGeneratePlanOpen(false)}
          userId={userId}
          customer={customer}
          mealTimeId={activeMealTimeId}
          mealTimes={mealTimes}
          createMode={isMealFoodMode ? "meal" : "strict"}
          planType={planType}
          onCreated={() => {
            refreshAfterDietMutation();
          }}
          enabled={supportsFoodAdd}
        />
      )}

      <AddFoodByRecipeModal
        open={addRecipeOpen}
        onClose={() => setAddRecipeOpen(false)}
        userId={userId}
        mealTimeId={activeMealTimeId}
        mealTimes={mealTimes}
        createMode={isMealFoodMode ? "meal" : "strict"}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
        enabled={supportsFoodAdd}
        planRole={foodPlanRole}
      />

      <ModerateAddModal
        open={moderateAddOpen}
        onClose={() => setModerateAddOpen(false)}
        userId={userId}
        mealTimeId={activeMealTimeId}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
        enabled={planType === 2 && !moderateHasActive}
      />

      <ModerateEditModal
        open={moderateEditOpen}
        onClose={() => {
          setModerateEditOpen(false);
          setModerateEditPlan(null);
        }}
        plan={moderateEditPlan}
        onSaved={() => {
          refreshAfterDietMutation();
        }}
      />

      <RelaxAddModal
        open={relaxAddOpen}
        onClose={() => setRelaxAddOpen(false)}
        userId={userId}
        enabled={planType === 3 && !relaxHasActive}
        onCreated={() => {
          refreshAfterDietMutation();
        }}
      />

      <RelaxEditModal
        open={relaxEditOpen}
        onClose={() => {
          setRelaxEditOpen(false);
          setRelaxEditPlan(null);
        }}
        plan={relaxEditPlan}
        onSaved={() => {
          refreshAfterDietMutation();
        }}
      />

      <DeleteConfirmModal />

      <PlanVersionHistoryModal
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        userId={userId}
        planType={planType}
        canEdit={canEdit}
      />
    </div>
  );
};

export default DietPlanTab;
